import { getRecoil, setRecoil } from "recoil-nexus";
import { userStore } from "../stores/access";
import { configStore } from "../stores/config";
import { notyStore } from "../stores/notys";
import cloneDeep from "lodash/cloneDeep";
import { paymentStore } from "../stores/payments";
import { playerPageDefaultState, playerPageStore } from "../stores/playerPage";
import { routeStore } from "../stores/route";
import { playerGameLogStore } from "../stores/playerGameLog";
import { gameStore } from "../stores/games";
import { loaderStore, topMenuStore } from "../stores/interface";

import { applyMode, applyDensity, Density, Mode } from "@cloudscape-design/global-styles";

/* Modules */
import Flat from "../modules/Flat";
import Stats from "../modules/Stats";
import Reports from "../modules/Reports";
import PlayersStats from "../modules/PlayersStats";
import Withdrawals from "../modules/Withdraws";
import Games from "../modules/Games";
import Players from "../modules/Players";
import PlayersSection from "../modules/PlayersSection";
import ReferralCheck from "../modules/ReferralCheck";
import Postbacks from "../modules/Postbacks";
import Chat from "../modules/Chat";
import Staff from "../modules/Staff";
import StaffCreditLimits from "../modules/StaffCreditLimits";
import Settings from "../modules/Settings";
import WheelSettings from "../modules/WheelSettings";
import Bonuses from "../modules/Bonuses";
import Exports from "../modules/Exports";
import Domains from "../modules/Domains";
import { playerSessionsModalStore } from "../stores/playerSessionsModal";
import * as pako from "pako";
import GamesSection from "../modules/GamesSection";
import Badges from "../modules/Badges";
import { gameSectionStore } from "../stores/gamesSection";
import { badgesStore } from "../stores/badges";
import FortuneWheel from "../modules/FortuneWheel";
import { FortuneWheelStore } from "../stores/fortuneWheel";
import fortuneWheelModal from "../modules/FortuneWheelModal";
import { fortuneWheelModalStore } from "../stores/fortuneWheelModal";
import { bonusesStore } from "../stores/bonuses";
import { taskManagerStore } from "../stores/taskManager";
import Translation from "../modules/Translation";
import Banners from "../modules/Banners";
import HomeBanners from "../modules/HomeBanners";
import ModalOffers from "../modules/ModalOffers";
import BannersAnalytics from "../modules/BannersAnalytics";
import MenuBannersAnalytics from "../modules/MenuBannersAnalytics";
import { bannersStore } from "../stores/banners";
import { homeBannersStore } from "../stores/homeBanners";
import { analyticsBannerInfoModalStore, analyticsBannersStore, analyticsMenuBannerInfoModalStore, analyticsMenuBannersStore } from "../stores/analyticsBanners";
import { tournamentsStore } from "../stores/tournaments";
import Tournaments from "../modules/Tournaments";
import { blackListWithdrawalsStore } from "../stores/blackListWithdrawals";
import BlackListWithdrawals from "../modules/BlackListWithdrawals";
import { blackListLogWithdrawalsStore } from "../stores/blackListLogWithdrawals";
import BlackListLogWithdrawals from "../modules/BlackListLogWithdrawals";
import WithdrawalsLimitLogs from "../modules/WithdrawalsLimitLogs";
import { whiteListPlayersWithdrawalStore } from "../stores/whiteListPlayersWithdrawals";
import WhiteListPlayersWithdrawals from "../modules/WhiteListPlayersWithdrawals";
import { tournamentsBannersStore } from "../stores/tournamentsBanners";
import TournamentsBanners from "../modules/TournamentsBanners";
import { promoBannersStore } from "../stores/promoBanners";
import PromoBanners from "../modules/PromoBanners";
import { promoBonusesStore } from "../stores/promoBonuses";
import PromoBonuses from "../modules/PromoBonuses";
import { personalPromotionsStore } from "../stores/personalPromotions";
import PersonalPromotions from "../modules/PersonalPromotions";
import PersonalPromotionsFormModal from "../modules/PersonalPromotionsFormModal";
import VipFreespins from "../modules/VipFreespins";
import WelcomeFreespins from "../modules/WelcomeFreespins";
import { playerBalanceLogStore } from "../stores/playerBalanceLog";
import { userActionLogStore } from "../stores/userActionLog";
import PlayerBalanceLog from "../modules/PlayerBalanceLog";
import { chatPinnedStore } from "../stores/chatPinned";
import ChatPinned from "../modules/ChatPinned";
import { multiWithdrawalStore } from "../stores/multiWithdrawal";
import { staffLogModalStore } from "../stores/staffLogModal";
import { staffDetailLogStore } from "../stores/staffDetailLog";
import { promotionStore } from "../stores/promotion";
import Promotion from "../modules/Promotion";
import { promotionConfigModalStore } from "../stores/promotionConfigModal";
import WalletPromotion from "../modules/WalletPromotion";
import { walletPromotionStore } from "../stores/walletPromotion";
import WalletGroups from "../modules/WalletGroups";
import { walletGroupsStore } from "../stores/walletGroups";
import DepositLadder from "../modules/DepositLadder";
import { depositLadderArchiveStore, depositLadderStatisticsStore, depositLadderStatisticsUsersStore, depositLadderStore } from "../stores/depositLadder";
import TgBots from "../modules/TgBots";
import SmsRouting from "../modules/SmsRouting";
import { tgBotsStore } from "../stores/tgBots";
import { createTgBotsRotationState, tgBotsRotationStore } from "../stores/tgBotsRotation";
import gameDetailLogModal from "../modules/GameDetailLogModal";
import { gameDetailLogModalStore } from "../stores/gameDetailLogModal";
import { providerOptionsStore, providerStore } from "../stores/provider";
import Providers from "../modules/Providers";
import { clientStore } from "../stores/client";
import ClientsPage from "../modules/Clients";
import { providerLogStore } from "../stores/providerLog";
import ProvidersLog from "../modules/ProvidersLog";

import { brandLicensesStore } from "../stores/brandLicenses";
import BrandLicenses from "../modules/BrandLicenses";
import { panelPaymentsLogStore } from "../stores/panelPaymentsLog";
import PanelPaymentsUsers from "../modules/PanelPaymentsUsers";
import { panelPaymentsUsersStore } from "../stores/panelPaymentsUsers";
import PlayerRules from "../modules/PlayerRules";
import { playerRulesStore } from "../stores/playerRules";
import { playerTagStore } from "../stores/playerTag";
import PlayerTag from "../modules/PlayerTag";
import { notificationsStore } from "../stores/notifications";
import { notificationTagsStore } from "../stores/notificationTags";
import Notifications from "../modules/Notifications";
import WebhookBroadcasts from "../modules/WebhookBroadcasts";
import { webhookBroadcastsStore } from "../stores/webhookBroadcasts";
import { webhookPresetsStore } from "../stores/webhookPresets";
import { groupStore } from "../stores/group";
import Group from "../modules/Group";
import { playerRulesAutomaticLogStore } from "../stores/playerRulesAutomaticLog";
import PlayerRulesAutomaticLog from "../modules/PlayerRulesAutomaticLog";
import { playerBanStore } from "../stores/playerBan";
import PlayerBan from "../modules/PlayerBan";
import { playerHidePaywayStore } from "../stores/playerHidePayway";
import PlayerHidePayway from "../modules/PlayerHidePayway";
import { gamesTagsStore, gamesMetaTagsStore } from "../stores/gamesTagsModal";
import Achievement from "../modules/Achievement";
import ABTests from "../modules/ABTests";
import SportsbookTags from "../modules/SportsbookTags";
import { achievementStore } from "../stores/achievement";
import { achievementCategoryStore } from "../stores/achievementCategory";
import { prizeStore } from "../stores/prize";
import Prize from "../modules/Prize";
import { chestPrizeStore } from "../stores/chestPrize";
import ChestPrize from "../modules/ChestPrize";
import { partnerSettingsStore } from "../stores/partnerSettings";

const PANEL_PAYMENTS_LOG_POLL_MS = 2000;

class Interface {
	constructor() {
		this.routes = [
			{ title: "Home", path: "/", level: 1, component: Flat, global: true, permission: "home" },
			{ title: "Stats", path: "/stats", level: 6, component: Stats, group: "Finance", global: false, permission: "stats" },
			{ title: "Reports", path: "/reports", level: 6, component: Reports, group: "Finance", global: true, permission: "reports" },
			{ title: "Player's stats", path: "/pstats", level: 6, component: PlayersStats, group: "Finance", global: false, permission: "player_stats" },
			{ title: "Withdrawals", path: "/withdrawals", level: 4, component: Withdrawals, group: "Payments", global: false, permission: "withdrawals" },
			{ title: "Payments log", path: "/payments_log", level: 6, component: PanelPaymentsUsers, group: "Payments", global: true, permission: "payments_log" },
			{ title: "Wallet groups", path: "/wallet-groups", level: 6, component: WalletGroups, group: "Payments", global: false, permission: "wallet_groups" },
			{ title: "Black list", path: "/black-list-withdrawals", level: 6, component: BlackListWithdrawals, group: "AntiFraud", global: false, permission: "blacklist_players" },
			{ title: "Black list log", path: "/blacklist-log-withdrawals", level: 6, component: BlackListLogWithdrawals, group: "AntiFraud", global: false, permission: "blacklist_players_log" },
			{ title: "White list withdrawal limit", path: "/whitelist-withdrawal", level: 6, component: WhiteListPlayersWithdrawals, group: "AntiFraud", global: false, permission: "whitelist_limit_players" },
			{ title: "Withdrawal limit log", path: "/withdrawals-limit", level: 6, component: WithdrawalsLimitLogs, group: "AntiFraud", global: false, permission: "whitelist_limit_players_log" },
			{ title: "Downgrade Balance Log", path: "/downgrade-balance-log", level: 6, component: PlayerBalanceLog, group: "AntiFraud", global: false, permission: "downgrade_balance_players_log" },
			{ title: "Player rules", path: "/player-rules", level: 6, component: PlayerRules, group: "AntiFraud", global: true, permission: "antifraud_player_rules" },
			{ title: "Player tag", path: "/player-tag", level: 6, component: PlayerTag, group: "AntiFraud", global: true, permission: "antifraud_player_tag" },
			{ title: "Player log automatic", path: "/player-rules-automatic", level: 6, component: PlayerRulesAutomaticLog, group: "AntiFraud", global: true, permission: "antifraud_player_log_automatic" },
			{ title: "Bonuses", path: "/bonuses", level: 3, component: Bonuses, group: "Promo", global: false, permission: "bonuses" },
			{ title: "Achievement", path: "/achievement", level: 6, component: Achievement, group: "Promo", global: false, permission: "achievement" },
			{ title: "Prize", path: "/prize", level: 6, component: Prize, group: "Promo", global: false, permission: "prize" },
			{ title: "Chest Prize", path: "/chest-prize", level: 6, component: ChestPrize, group: "Promo", global: false, permission: "chest_catalog" },
			{ title: "Promotion", path: "/promotion", level: 3, component: Promotion, group: "Promo", global: false, permission: "promotion" },
			{ title: "Wallet Promotions", path: "/wallet-promotions", level: 3, component: WalletPromotion, group: "Promo", global: false, permission: "promotion" },
			{ title: "Deposit Ladder", path: "/deposit-ladder", level: 3, component: DepositLadder, group: "Promo", global: false, permission: "deposit_ladder" },
			{ title: "Personal Promotions", path: "/personal-promotions", level: 3, component: PersonalPromotions, group: "Promo", global: false, permission: "personal_promotions" },
			{ title: "VIP FreeSpins", path: "/vip-freespins", level: 6, component: VipFreespins, group: "Promo", global: false, permission: "vip_freespins" },
			{ title: "Welcome Freespins", path: "/welcome-freespins", level: 6, component: WelcomeFreespins, group: "Promo", global: false, permission: "welcome_freespins" },
			{ title: "FortuneWheel", path: "/fortunewheel", level: 2, component: FortuneWheel, group: "Promo", global: false, permission: "fortunewheel" },
			{ title: "Banners", path: "/banners", level: 2, component: Banners, group: "Promo", global: false, permission: "banners" },
			{ title: "Banners Analytics", path: "/banners-analytics", level: 2, component: BannersAnalytics, group: "Promo", global: false, permission: "banners_analytics" },
			{ title: "Menu Banners Analytics", path: "/menu-banners-analytics", level: 2, component: MenuBannersAnalytics, group: "Promo", global: false, permission: "menu_banners_analytics" },
			{ title: "Menu Banners", path: "/home-banners", level: 2, component: HomeBanners, group: "Promo", global: false, permission: "home_banners" },
			{ title: "Modal offers", path: "/offer-modals", level: 2, component: ModalOffers, group: "Promo", global: false, permission: "offer-modals" },
			{ title: "Tournaments banners", path: "/tournaments-banners", level: 2, component: TournamentsBanners, group: "Promo", global: false, permission: "tournaments_banners" },
			{ title: "Promo banners", path: "/promo-banners", level: 2, component: PromoBanners, group: "Promo", global: false, permission: "promo_banners" },
			{ title: "Promo bonuses", path: "/promo-bonuses", level: 3, component: PromoBonuses, group: "Promo", global: false, permission: "promo_bonuses" },
			{ title: "Tournaments", path: "/tournaments", level: 2, component: Tournaments, group: "Promo", global: false, permission: "tournaments" },
			{ title: "Exports", path: "/exports", level: 6, component: Exports, group: "Data", global: false, permission: "exports" },
			{ title: "Games", path: "/games", level: 2, component: Games, group: "Data", global: true, permission: "games" },
			{ title: "Games Section", path: "/games-section", level: 3, component: GamesSection, group: "Data", global: true, permission: "games_section" },
			{ title: "Badges", path: "/badges", level: 3, component: Badges, group: "Data", global: true, permission: "badges" },
			{ title: "Providers", path: "/providers", level: 6, component: Providers, group: "Data", global: true, permission: "providers" },
			{ title: "ProviderLog", path: "/providers_log", level: 6, component: ProvidersLog, group: "Data", global: true, permission: "providers_log" },
			{ title: "Clients", path: "/clients", level: 6, component: ClientsPage, group: "Data", global: true, permission: "clients" },
			{ title: "Players", path: "/players", level: 2, component: Players, group: "Data", global: false, permission: "players" },
			{ title: "Players Sessions", path: "/players-sessions", level: 2, component: PlayersSection, group: "Data", global: false, permission: "players-sessions" },
			{ title: "A/B Tests", path: "/ab-tests", level: 6, component: ABTests, group: "Control", global: false, permission: "ab_test" },
			{ title: "Sportsbook groups", path: "/sportsbook-groups", level: 6, component: SportsbookTags, group: "Promo", global: false, permission: "sportsbook_tags" },
			{ title: "Referral Check", path: "/referral-check", level: 6, component: ReferralCheck, group: "Control", global: true, permission: "referral_check" },
			{ title: "Postbacks", path: "/postbacks", level: 6, component: Postbacks, group: "Data", global: false, permission: "postbacks" },
			{ title: "Chat", path: "/chat", level: 2, component: Chat, group: "Control", global: false, permission: "chat" },
			{ title: "Group", path: "/group", level: 6, component: Group, group: "Control", global: false, permission: "group" },
			{ title: "Chat pinned", path: "/chat-pinned", level: 3, component: ChatPinned, group: "Control", global: false, permission: "chat_pinned" },
			{ title: "Translation", path: "/translation", level: 3, component: Translation, group: "Control", global: false, permission: "translation" },
			{ title: "Staff", path: "/staff", level: 6, component: Staff, group: "Control", global: true, permission: "staff" },
			{ title: "Credit limits", path: "/staff-credit-limits", level: 6, component: StaffCreditLimits, group: "Control", global: true, showColumn: "balanceCreditLimits" },
			{ title: "Domains", path: "/domains", level: 6, component: Domains, group: "Control", global: false, permission: "domains" },
			{ title: "Telegram bots", path: "/telegram-bots", level: 6, component: TgBots, group: "Control", global: false, permission: "telegram_bots" },
			{ title: "OTP Routing", path: "/sms-routing", level: 6, component: SmsRouting, group: "Control", global: false, permission: "sms_routing" },
			{ title: "BrandLicenses", path: "/brand_licenses", level: 6, component: BrandLicenses, group: "Control", global: false, permission: "brand_licenses" },
			{ title: "Notifications", path: "/notifications", level: 6, component: Notifications, group: "Control", global: false, permission: "notifications" },
			{ title: "Webhook broadcasts", path: "/webhook-broadcasts", level: 6, component: WebhookBroadcasts, group: "Control", global: false, permission: "webhook_broadcasts" },
			{ title: "Player ban", path: "/player-ban", level: 6, component: PlayerBan, group: "Control", global: false, permission: "player_ban" },
			{ title: "Player hide payway", path: "/player-hide-payway", level: 6, component: PlayerHidePayway, group: "Control", global: false, permission: "player_hide_payway" },
			{ title: "Settings", path: "/settings", level: 6, component: Settings, group: "Control", global: false, permission: "settings" },
			{ title: "GEO rules", path: "/geo-rules", level: 6, component: WheelSettings, group: "Control", global: false, showColumn: "geoRules" },
			{ title: "Dark mode", level: 1, path: "/mode-dark", group: "Mode", global: true, permission: "mode_dark" },
			{ title: "Light mode", level: 1, path: "/mode-light", group: "Mode", global: true, permission: "mode_light" },
		];

		this.activeAbortControllers = new Set();
		this.playerInfoRequestId = 0;
		this.playerLogRequestId = 0;
		this.partnerSettingsRequestId = 0;
		this.notificationsListRequestId = 0;
		this.notificationQueueRequestId = 0;
		this.gameLookupSource = null;
		this.gameNameLookup = new Map();
	}

	hasShowColumnAccess(route, userShowColumn = []) {
		if (!route.showColumn) return true;

		return Array.isArray(userShowColumn) && userShowColumn.includes(route.showColumn);
	}

	hasRouteAccess(route, userLevel, userPermissions, userShowColumn = []) {
		const hasLevelAccess = route.level <= userLevel;

		if (route.showColumn) {
			return hasLevelAccess || this.hasShowColumnAccess(route, userShowColumn);
		}

		return hasLevelAccess || userPermissions.includes(route.permission);
	}

	filterRoutes(routes, userLevel, userPermissions, userShowColumn = []) {
		return routes.filter((route) => this.hasRouteAccess(route, userLevel, userPermissions, userShowColumn));
	}

	getRoutes(userLevel, userPermissions, userShowColumn = []) {
		return this.filterRoutes(this.routes, userLevel, userPermissions, userShowColumn);
	}

	decompressString(compressedString) {
		const binaryString = window.atob(compressedString);
		const charData = binaryString.split("").map((c) => c.charCodeAt(0));
		const binaryArray = new Uint8Array(charData);

		return pako.inflate(binaryArray, { to: "string" });
	}

	loadBrands() {
		setRecoil(userStore, { ...getRecoil(userStore), process: true });

		this.request("get", { class: "data", module: "brands", method: "list" })
			.then((brands) => {
				localStorage.setItem("access.brandsList", JSON.stringify(brands));
				setRecoil(userStore, { ...getRecoil(userStore), brandsList: brands, process: false });
			})
			.catch(() => {
				setRecoil(userStore, { ...getRecoil(userStore), process: false });
			});
	}

	checkMenuAction(action) {
		switch (action) {
			case "logout":
				setRecoil(userStore, { ...getRecoil(userStore), token: false, level: 0, password: "" });
				localStorage.removeItem("access.token");
				localStorage.removeItem("access.level");
				localStorage.removeItem("menu.top");
				setRecoil(topMenuStore, []);
				setRecoil(routeStore, { currentPath: "/" });
				break;
			case "mode-dark":
				localStorage.setItem("mode", "dark");
				//applyMode(Mode.Dark);
				window.location.reload();
				break;
			case "mode-light":
				localStorage.setItem("mode", "light");
				//applyMode(Mode.Light);
				window.location.reload();
				break;
		}
	}

	fmt = (v) => (v != null ? new Intl.NumberFormat("ru-RU").format(v) : "0");
	fmtMoney = (cents, isFs = false) => (cents > 0 ? `${new Intl.NumberFormat("ru-RU").format(Math.round(isFs ? cents : cents / 100))} RUB.` : "0 RUB.");
	fmtTickets = (n) => (n > 0 ? `${this.fmt(n)} tickets` : "0 tickets");
	fmtFs = (n) => (n > 0 ? `${this.fmt(n)} FS` : "0 FS");

	arraysAreEqual(arr1, arr2) {
		if (arr1.length !== arr2.length) {
			return false;
		}

		for (let i = 0; i < arr1.length; i++) {
			if (arr1[i] !== arr2[i]) {
				return false;
			}
		}

		return true;
	}

	initData() {
		fetch(getRecoil(configStore).routerApi.servlet + "/?method=domain&project=" + getRecoil(configStore).routerApi.id)
			.then((response) => response.json())
			.then((data) => {
				fetch(getRecoil(configStore).routerApi.servlet + "/?method=domain&project=" + getRecoil(configStore).routerApi.id + "_5")
					.then((response) => response.json())
					.then((mData) => {
						fetch(getRecoil(configStore).routerApi.servlet + "/?method=domain&project=" + getRecoil(configStore).routerApi.id + "_3")
							.then((response) => response.json())
							.then((tgData) => {
								fetch(getRecoil(configStore).routerApi.servlet + "/?method=domain&project=" + getRecoil(configStore).routerApi.id + "_4")
									.then((response) => response.json())
									.then((vkData) => {
										let link = data.success ? data.data : "#",
											mirror = mData.success ? mData.data : "#",
											tgLink = tgData.success ? tgData.data : "#",
											vkLink = vkData.success ? vkData.data : "#";

										setRecoil(configStore, { ...getRecoil(configStore), domain: link });

										let menu = cloneDeep(getRecoil(topMenuStore));

										let groups = {};

										this.routes.forEach((route) => {
											if (typeof route.group !== "undefined") {
												if (typeof groups[route.group] === "undefined")
													groups[route.group] = {
														type: "menu-dropdown",
														variant: "link",
														onItemClick: (item) => {
															this.checkMenuAction(item.detail.id);
														},
														routing: true,
														items: [],
														text: route.group,
													};

												if (route.group === "Mode") {
													if (route.title === "Dark mode") {
														if (document.body.classList[0] === "awsui-dark-mode") {
															groups[route.group].items.push({ type: "button", variant: "link", text: route.title, disabled: true, disabledReason: "Already using", badge: true, id: route.path.slice(1) });
														} else groups[route.group].items.push({ type: "button", variant: "link", text: route.title, badge: true, id: route.path.slice(1) });
													} else {
														if (typeof document.body.classList[0] !== "undefined") {
															groups[route.group].items.push({ type: "button", variant: "link", text: route.title, badge: true, id: route.path.slice(1) });
														} else groups[route.group].items.push({ type: "button", variant: "link", text: route.title, disabled: true, disabledReason: "Already using", badge: true, id: route.path.slice(1) });
													}
												} else {
													if (this.hasRouteAccess(route, getRecoil(userStore).level, getRecoil(userStore).permission, getRecoil(userStore).showColumn) && (getRecoil(userStore).mode === "brand" || route.global)) groups[route.group].items.push({ type: "button", variant: "link", text: route.title, badge: true, path: route.path, id: route.path.slice(1) });
												}
											} else this.hasRouteAccess(route, getRecoil(userStore).level, getRecoil(userStore).permission, getRecoil(userStore).showColumn) && menu.push({ type: "button", variant: "link", text: route.title, badge: true, path: route.path });
										});

										Object.keys(groups).forEach((key) => {
											if (groups[key].items.length) menu.push(groups[key]);
										});

										if (getRecoil(userStore).mode === "brand")
											menu.push({
												type: "menu-dropdown",
												variant: "link",
												items: [
													{ id: "1", external: true, text: getRecoil(userStore).brand.name, href: "https://" + link },
													{ id: "1", external: true, text: "Mirror", href: "https://" + mirror },
													{ id: "3", external: true, text: "VK group", href: "https://" + vkLink },
													{ id: "4", external: true, text: "Telegram channel", href: "https://" + tgLink },
												],
												text: "Links",
												badge: true,
											});

										menu.push({
											type: "menu-dropdown",
											onItemClick: (item) => {
												this.checkMenuAction(item.detail.id);
											},
											variant: "link",
											items: [{ id: "logout", external: true, text: "Logout" }],
											text: localStorage.getItem("access.login"),
										});

										//localStorage.setItem('menu.top', JSON.stringify([...getRecoil(topMenuStore), ...menu]));
										setRecoil(topMenuStore, [...getRecoil(topMenuStore), ...menu]);
									});
							});
					});
			});
	}

	loadAchievementCategoryList(project) {
		setRecoil(achievementCategoryStore, { ...getRecoil(achievementCategoryStore), ...getRecoil(achievementCategoryStore), loaded: false });

		this.request("get", { class: "data", module: "achievementCategory", method: "list" }, { sorting: getRecoil(achievementCategoryStore).sorting.sortingColumn, search: getRecoil(achievementCategoryStore).currentSearch, page: getRecoil(achievementCategoryStore).pagination.current, limit: getRecoil(configStore).achievementCategory.perPage, project: typeof project !== "undefined" ? project : 0 })
			.then((playerRules) => {
				setRecoil(achievementCategoryStore, {
					...getRecoil(achievementCategoryStore),
					...getRecoil(achievementCategoryStore),
					pagination: {
						current: playerRules.pagination.page,
						total: playerRules.pagination.totalPages,
						count: playerRules.pagination.total,
					},
					error: {
						text: "",
						code: 0,
						timestamp: 0,
					},
					data: playerRules.items,
					loaded: true,
				});
			})
			.catch((e) => {
				console.log(e);
				setRecoil(achievementCategoryStore, { ...getRecoil(achievementCategoryStore), ...getRecoil(achievementCategoryStore), loaded: true, error: e });
			});
	}

	loadPrizeList(project) {
		setRecoil(prizeStore, { ...getRecoil(prizeStore), ...getRecoil(prizeStore), loaded: false });

		this.request("get", { class: "data", module: "prize", method: "list" }, { sorting: getRecoil(prizeStore).sorting.sortingColumn, filters: getRecoil(prizeStore).filters, search: getRecoil(prizeStore).currentSearch, page: getRecoil(prizeStore).pagination.current, limit: getRecoil(configStore).prize.perPage, project: typeof project !== "undefined" ? project : 0 })
			.then((prizeList) => {
				setRecoil(prizeStore, {
					...getRecoil(prizeStore),
					...getRecoil(prizeStore),
					pagination: {
						current: prizeList.pagination.page,
						total: prizeList.pagination.pages,
						count: prizeList.pagination.total,
					},
					error: {
						text: "",
						code: 0,
						timestamp: 0,
					},
					data: prizeList.items,
					loaded: true,
				});
			})
			.catch((e) => {
				console.log(e);
				setRecoil(prizeStore, { ...getRecoil(prizeStore), ...getRecoil(prizeStore), loaded: true, error: e });
			});
	}

	loadChestPrizeList(project) {
		setRecoil(chestPrizeStore, { ...getRecoil(chestPrizeStore), ...getRecoil(chestPrizeStore), loaded: false });

		this.request(
			"get",
			{ class: "data", module: "chestPrize", method: "list" },
			{
				sorting: getRecoil(chestPrizeStore).sorting.sortingColumn,
				search: getRecoil(chestPrizeStore).currentSearch,
				page: getRecoil(chestPrizeStore).pagination.current,
				limit: getRecoil(configStore).chestPrize.perPage,
				project: typeof project !== "undefined" ? project : 0,
			},
		)
			.then((chestPrizeList) => {
				setRecoil(chestPrizeStore, {
					...getRecoil(chestPrizeStore),
					...getRecoil(chestPrizeStore),
					pagination: {
						current: chestPrizeList.pagination.page,
						total: chestPrizeList.pagination.totalPages,
						count: chestPrizeList.pagination.total,
					},
					error: {
						text: "",
						code: 0,
						timestamp: 0,
					},
					data: chestPrizeList.items,
					loaded: true,
				});
			})
			.catch((error) => {
				console.log(error);
				setRecoil(chestPrizeStore, { ...getRecoil(chestPrizeStore), ...getRecoil(chestPrizeStore), loaded: true, error });
			});
	}

	getChestPrizeOptions({ page = 1, limit = 999, search = "", project = 0 } = {}) {
		return this.request(
			"get",
			{ class: "data", module: "chestPrize", method: "list" },
			{
				sorting: { column: "id", order: "DESC" },
				search,
				page,
				limit,
				project,
			},
		);
	}

	prizeGetStatistic({ period, start_date, end_date }) {
		return new Promise((resolve, reject) => {
			this.request("get", { class: "data", module: "prize", method: "getStatistic" }, { period, start_date, end_date, project: 0 })
				.then((statistic) => {
					resolve(statistic);
				})
				.catch((error) => {
					reject(error);
				});
		});
	}

	prizeItemWinners({ page = 1, limit = 20, filters = {}, sorting = null } = {}) {
		return this.request("get", { class: "data", module: "prize", method: "itemWinners" }, { page, limit, ...filters, sorting, project: 0 });
	}

	prizeUpdateItemWinnerStatus({ id, roundId, status }) {
		return this.request("set", { class: "data", module: "prize", method: "updateItemWinnerStatus" }, { id, roundId, status, project: 0 });
	}

	prizeGetTypeOrder(project) {
		return new Promise((resolve, reject) => {
			this.request("get", { class: "data", module: "prize", method: "getTypeOrder" }, { project: typeof project !== "undefined" ? project : 0 })
				.then((order) => {
					resolve(order);
				})
				.catch((error) => {
					reject(error);
				});
		});
	}

	prizeUpdateTypeOrder(order, project) {
		return new Promise((resolve, reject) => {
			this.request("set", { class: "data", module: "prize", method: "updateTypeOrder" }, { order, project: typeof project !== "undefined" ? project : 0 })
				.then((updatedOrder) => {
					resolve(updatedOrder);
				})
				.catch((error) => {
					reject(error);
				});
		});
	}
	loadAchievementStatistics({ period, start_date, end_date }) {
		return new Promise((resolve, reject) => {
			this.request("get", { class: "data", module: "achievement", method: "getStatistic" }, { period, start_date, end_date, project: 0 })
				.then((statistic) => {
					resolve(statistic);
				})
				.catch((error) => {
					reject(error);
				});
		});
	}
	loadAchievementList(project) {
		setRecoil(achievementStore, { ...getRecoil(achievementStore), ...getRecoil(achievementStore), loaded: false });

		this.request("get", { class: "data", module: "achievement", method: "list" }, { sorting: getRecoil(achievementStore).sorting.sortingColumn, search: getRecoil(achievementStore).currentSearch, page: getRecoil(achievementStore).pagination.current, limit: getRecoil(configStore).achievement.perPage, project: typeof project !== "undefined" ? project : 0 })
			.then((playerRules) => {
				setRecoil(achievementStore, {
					...getRecoil(achievementStore),
					...getRecoil(achievementStore),
					pagination: {
						current: playerRules.pagination.page,
						total: playerRules.pagination.pages,
						count: playerRules.pagination.total,
					},
					error: {
						text: "",
						code: 0,
						timestamp: 0,
					},
					data: playerRules.items,
					statusCounts: {
						active: Number(playerRules.statusCounts?.active || 0),
						disabled: Number(playerRules.statusCounts?.disabled || 0),
					},
					loaded: true,
				});
			})
			.catch((e) => {
				console.log(e);
				setRecoil(achievementStore, { ...getRecoil(achievementStore), ...getRecoil(achievementStore), loaded: true, error: e });
			});
	}

	loadAchievementExport(filters, project) {
		return this.request(
			"get",
			{ class: "data", module: "achievement", method: "export" },
			{
				filters,
				project: typeof project !== "undefined" ? project : 0,
			},
		).then((result) => {
			console.log("result: ", result);
			return result.items || [];
		});
	}

	updateGameData(gameID, data) {
		let games = cloneDeep(getRecoil(gameStore).data);
		games.forEach((game) => {
			if (parseInt(game.gameID) === parseInt(gameID)) {
				game.name = data.name;
				game.providerID = data.providerID;
				game.poster = game.poster;
				game.bigPoster = game.bigPoster;
				game.categoryID = data.categoryID;
				game.active = data.active ? 1 : 0;
				game.rtp = data.rtp;
				game.biases = { ...game.biases, bias: data.bias };
				game.sectionIDs = Array.isArray(data.sectionIDs) ? data.sectionIDs : game.sectionIDs || [];
				game.badges = Array.isArray(data.badges) ? data.badges : game.badges || [];
			}
		});

		setRecoil(gameStore, { ...getRecoil(gameStore), data: games });
	}

	loadCasinoData() {
		setRecoil(gameStore, { ...getRecoil(gameStore), loaded: false, selectedItems: [] });

		this.request("get", { class: "casino", module: "game", method: "list" }, { providerID: 0 }).then((list) => {
			let games = [];
			list.data = JSON.parse(this.decompressString(list.data));
			if (getRecoil(userStore).level >= 6)
				this.request("get", { class: "data", module: "games", method: "stats" }, { gameID: "all", limit: 1, offset: 0 }).then((stats) => {
					list.data.forEach((game) => {
						if (typeof stats[0] !== "undefined" && typeof stats[0].data !== "undefined")
							stats[0].data.forEach((stat) => {
								if (parseInt(stat.gameID) === parseInt(game.id)) {
									game.bets = {};
									game.wins = {};

									game.bets.total = parseFloat(parseFloat(stat.bets.total / 100).toFixed(2));
									game.wins.total = parseFloat(parseFloat(stat.wins.total / 100).toFixed(2));
									game.bets.count = stat.bets.count;
									game.rtp = parseFloat((parseFloat(stat.wins.total / stat.bets.total) * 100).toFixed(2));
								}
							});

						if (typeof game.rtp === "undefined") game.rtp = 0;

						if (typeof game.bets === "undefined") game.bets = { total: 0, count: 0 };

						if (typeof game.wins === "undefined") game.wins = { total: 0, count: 0 };

						const tags = game.tags?.split(",").map((tag) => tag.trim()) || [];
						const metaTags = game.metaTags?.split(",").map((tag) => tag.trim()) || [];
						games.push({
							name: game.params.name,
							integrator: this.getProviderIntegratorByID(game.providerID),
							bets: game.bets,
							wins: game.wins,
							rtp: game.rtp,
							poster: game.params.poster,
							bigPoster: game.params.bigPoster,
							gameID: game.id,
							providerID: game.providerID,
							categoryID: game.categoryID,
							biases: game.biases,
							date: game.date * 1000,
							active: game.active,
							filtered: true,
							tags,
							metaTags,
							sectionIDs: Array.isArray(game.sectionIDs) ? game.sectionIDs : [],
							badges: Array.isArray(game.badges) ? game.badges : [],
						});
					});

					setRecoil(gameStore, { ...getRecoil(gameStore), loaded: true, data: games, pagination: { current: getRecoil(gameStore).pagination.current, total: Math.ceil(games.length / getRecoil(configStore).game.perPage) } });

					let status = cloneDeep(getRecoil(configStore).game.options.status);
					status[0].value = "All games (" + games.length + ")";
					status[1].value =
						"Enabled (" +
						games.filter((game) => {
							if (game.active === 1) return true;
						}).length +
						")";
					status[2].value =
						"Disabled (" +
						games.filter((game) => {
							if (game.active !== 1) return true;
						}).length +
						")";

					setRecoil(configStore, { ...getRecoil(configStore), game: { ...getRecoil(configStore).game, options: { ...getRecoil(configStore).game.options, status } } });

					this.request("get", { class: "casino", module: "provider", method: "list" }, { providerID: 0 }).then((list) => {
						let providers = [],
							providersOptions = [];

						providersOptions.push({ value: "All providers (" + list.data.length + ")", type: false });

						list.data.forEach((provider) => {
							providers.push({ id: provider.id, name: provider.name, path: provider.path, integrator: provider.integrator });
							providersOptions.push({
								path: provider.path,
								value:
									provider.name +
									" (" +
									games.filter((game) => {
										if (game.providerID === provider.id) return true;
									}).length +
									")",
								labelTag: "#" + provider.id,
								type: provider.id,
							});
						});

						setRecoil(gameStore, { ...getRecoil(gameStore), providers });
						setRecoil(configStore, { ...getRecoil(configStore), game: { ...getRecoil(configStore).game, options: { ...getRecoil(configStore).game.options, provider: providersOptions } } });

						this.request("get", { class: "casino", module: "category", method: "list" }, { providerID: 0 }).then((list) => {
							let categories = [],
								categoriesOptions = [];

							categoriesOptions.push({ value: "All categories (" + list.data.length + ")", type: false });

							list.data.forEach((category) => {
								categories.push({ id: category.id, name: category.name });
								categoriesOptions.push({
									value:
										category.name +
										" (" +
										games.filter((game) => {
											if (game.categoryID.toString().indexOf(category.id) !== -1) return true;
										}).length +
										")",
									labelTag: "#" + category.id,
									type: category.id,
								});
							});

							setRecoil(gameStore, { ...getRecoil(gameStore), categories });
							setRecoil(configStore, { ...getRecoil(configStore), game: { ...getRecoil(configStore).game, options: { ...getRecoil(configStore).game.options, category: categoriesOptions } } });
						});
					});
				});
			else {
				list.data.forEach((game) => {
					if (typeof game.rtp === "undefined") game.rtp = 0;

					if (typeof game.bets === "undefined") game.bets = { total: 0, count: 0 };

					if (typeof game.wins === "undefined") game.wins = { total: 0, count: 0 };

					const tags = game.tags?.split(",").map((tag) => tag.trim()) || [];
					const metaTags = game.metaTags?.split(",").map((tag) => tag.trim()) || [];

					games.push({
						name: game.params.name,
						bets: game.bets,
						integrator: this.getProviderIntegratorByID(game.providerID),
						wins: game.wins,
						rtp: game.rtp,
						poster: game.params.poster,
						bigPoster: game.params.bigPoster,
						gameID: game.id,
						providerID: game.providerID,
						categoryID: game.categoryID,
						date: game.date * 1000,
						biases: game.biases,
						active: game.active,
						filtered: true,
						tags,
						metaTags,
						sectionIDs: Array.isArray(game.sectionIDs) ? game.sectionIDs : [],
						badges: Array.isArray(game.badges) ? game.badges : [],
					});
				});

				setRecoil(gameStore, { ...getRecoil(gameStore), loaded: true, data: games, pagination: { current: 1, total: Math.ceil(games.length / getRecoil(configStore).game.perPage) } });

				let status = cloneDeep(getRecoil(configStore).game.options.status);
				status[0].value = "All games (" + games.length + ")";
				status[1].value =
					"Enabled (" +
					games.filter((game) => {
						if (game.active === 1) return true;
					}).length +
					")";
				status[2].value =
					"Disabled (" +
					games.filter((game) => {
						if (game.active !== 1) return true;
					}).length +
					")";

				setRecoil(configStore, { ...getRecoil(configStore), game: { ...getRecoil(configStore).game, options: { ...getRecoil(configStore).game.options, status } } });

				this.request("get", { class: "casino", module: "provider", method: "list" }, { providerID: 0 }).then((list) => {
					let providers = [],
						providersOptions = [];

					providersOptions.push({ value: "All providers (" + list.data.length + ")", type: false });

					list.data.forEach((provider) => {
						providers.push({ id: provider.id, name: provider.name, path: provider.path, integrator: provider.integrator });
						providersOptions.push({
							path: provider.path,
							value:
								provider.name +
								" (" +
								games.filter((game) => {
									if (game.providerID === provider.id) return true;
								}).length +
								")",
							labelTag: "#" + provider.id,
							type: provider.id,
						});
					});

					setRecoil(gameStore, { ...getRecoil(gameStore), providers });
					setRecoil(configStore, { ...getRecoil(configStore), game: { ...getRecoil(configStore).game, options: { ...getRecoil(configStore).game.options, provider: providersOptions } } });

					this.request("get", { class: "casino", module: "category", method: "list" }, { providerID: 0 }).then((list) => {
						let categories = [],
							categoriesOptions = [];

						categoriesOptions.push({ value: "All categories (" + list.data.length + ")", type: false });

						list.data.forEach((category) => {
							categories.push({ id: category.id, name: category.name });
							categoriesOptions.push({
								value:
									category.name +
									" (" +
									games.filter((game) => {
										if (game.categoryID.toString().indexOf(category.id) !== -1) return true;
									}).length +
									")",
								labelTag: "#" + category.id,
								type: category.id,
							});
						});

						setRecoil(gameStore, { ...getRecoil(gameStore), categories });
						setRecoil(configStore, { ...getRecoil(configStore), game: { ...getRecoil(configStore).game, options: { ...getRecoil(configStore).game.options, category: categoriesOptions } } });
					});
				});
			}
		});
	}

	loadPaymentData(brand) {
		if (typeof brand === "undefined") brand = getRecoil(userStore).brand.id;

		this.request("get", { class: "data", module: "wallet", method: "payways" }, { brand })
			.then((list) => {
				let payways = [{ value: "All methods", type: "all" }];
				list.forEach((payway) => {
					payways.push({ value: payway.method, type: payway.method });
				});

				setRecoil(loaderStore, { ...getRecoil(loaderStore), paymentData: true });
				setRecoil(configStore, { ...getRecoil(configStore), stats: { ...getRecoil(configStore).stats, options: { ...getRecoil(configStore).stats.options, payway: payways } }, pstats: { ...getRecoil(configStore).pstats, options: { ...getRecoil(configStore).pstats.options, payway: payways } } });
			})
			.catch(() => {
				setRecoil(loaderStore, { ...getRecoil(loaderStore), paymentData: true });
			});
	}

	loadGameMetaTags() {
		setRecoil(gamesMetaTagsStore, { ...getRecoil(gamesMetaTagsStore), loaded: false });
		const { filters } = getRecoil(gamesMetaTagsStore);
		this.request("get", { class: "data", module: "metatags", method: "list" }, { ...filters }).then((data) => {
			const { items, pagination } = data;

			setRecoil(gamesMetaTagsStore, (prev) => ({
				...prev,
				data: items,
				loaded: true,
				totalPages: pagination.pages,
				filters: { ...prev.filters, page: pagination.page, limit: pagination.limit },
			}));
		});
	}

	createGameMetaTag(metaTag) {
		return new Promise((resolve, reject) => {
			this.request("set", { class: "data", module: "metatags", method: "create" }, { ...metaTag, active: metaTag.active ? 1 : 0 })
				.then((data) => {
					this.loadGameMetaTags();
					resolve({ ...metaTag, id: data.insertId });
				})
				.catch((e) => {
					console.log(e);
					reject(e);
				});
		});
	}

	updateGameMetaTag(metaTag) {
		return new Promise((resolve, reject) => {
			this.request("set", { class: "data", module: "metatags", method: "update" }, { ...metaTag, active: metaTag.active ? 1 : 0 })
				.then((data) => {
					this.loadGameMetaTags();
					this.addNoty("Success", `Meta tag #${metaTag.id} updated`);
					resolve(data);
				})
				.catch((e) => {
					console.log(e);
					reject(e);
				});
		});
	}

	loadGameTags(metaTagId) {
		setRecoil(gamesTagsStore, { ...getRecoil(gamesTagsStore), loaded: false });
		const { filters } = getRecoil(gamesTagsStore);

		this.request("get", { class: "data", module: "tags", method: "list" }, { ...filters, metaTagId }).then((data) => {
			const { items, pagination } = data;

			setRecoil(gamesTagsStore, (prev) => ({
				...prev,
				data: items,
				loaded: true,
				totalPages: pagination.pages,
				filters: { ...prev.filters, page: pagination.page, limit: pagination.limit },
			}));
		});
	}

	loadGameTagsByIds(ids) {
		if (ids.length === 0) return Promise.resolve([]);
		return new Promise((resolve, reject) => {
			this.request("get", { class: "data", module: "tags", method: "getByIds" }, { ids })
				.then((data) => {
					resolve(data);
				})
				.catch((e) => {
					console.log(e);
					reject(e);
				});
		});
	}

	createGameTag(tag) {
		return new Promise((resolve, reject) => {
			this.request("set", { class: "data", module: "tags", method: "create" }, { ...tag, active: tag.active ? 1 : 0 })
				.then((data) => {
					this.loadGameTags(tag.metaTagId);
					this.addNoty("Success", "Tag created");
					resolve(data);
				})
				.catch((e) => {
					console.log(e);
					reject(e);
				});
		});
	}

	updateGameTag(tag) {
		return new Promise((resolve, reject) => {
			this.request("set", { class: "data", module: "tags", method: "update" }, { ...tag, active: tag.active ? 1 : 0 })
				.then((data) => {
					this.loadGameTags(tag.metaTagId);
					this.addNoty("Success", `Tag #${tag.id} updated`);
					resolve(data);
				})
				.catch((e) => {
					console.log(e);
					reject(e);
				});
		});
	}

	getGameByID(gameID) {
		const games = getRecoil(gameStore).data;

		if (this.gameLookupSource !== games) {
			this.gameLookupSource = games;
			this.gameNameLookup = new Map(games.map((game) => [String(game.gameID), `${game.name} (${game.gameID})`]));
		}

		return this.gameNameLookup.get(String(gameID)) || "Game";
	}

	getProviderByID(providerID) {
		let name = "Provider (" + providerID + ")";

		if (getRecoil(gameStore).providers.length)
			getRecoil(gameStore).providers.forEach((provider) => {
				if (provider.id === providerID) name = provider.name;
			});

		return name;
	}

	getProviderIntegratorByID(providerID) {
		let name = "Integrator (other)";

		if (getRecoil(gameStore).providers.length)
			getRecoil(gameStore).providers.forEach((provider) => {
				if (provider.id === providerID) {
					name = provider.integrator;
				}
			});

		return name;
	}

	getCategoryByID(categoryID) {
		let name = "Category (" + categoryID + ")";

		if (getRecoil(gameStore).categories.length)
			getRecoil(gameStore).categories.forEach((category) => {
				if (category.id.toString() === categoryID.toString()) name = category.name;
			});

		return name;
	}

	getSweepstakes() {
		return new Promise((resolve, reject) => {
			this.request(
				"get",
				{
					class: "data",
					module: "customGame",
					method: "getSweepstakes",
				},
				{},
			)
				.then((response) => {
					console.log(response);
					const source = response?.data || response || {};
					resolve({
						poster: source.poster || "",
						nameText: source.nameText || "",
						nameType: source.nameType || null,
						descriptionText: source.descriptionText || "",
						descriptionType: source.descriptionType || null,
						rtp: source.rtp ?? "",
						rtpLabelText: source.rtpLabelText || "",
						categoryIDs: Array.isArray(source.categoryIDs) ? source.categoryIDs.map((id) => Number(id)).filter((id) => !Number.isNaN(id)) : [],
						provider: {
							name: source.provider?.name || "",
							link: source.provider?.link || "",
						},
					});
				})
				.catch((error) => {
					console.log(error);
					reject({
						text: error?.text || error?.message || error?.toString() || "Unknown error",
						code: error?.code || 500,
						timestamp: Date.now(),
					});
				});
		});
	}

	updateSweepstakes(payload) {
		return new Promise((resolve, reject) => {
			this.request(
				"set",
				{
					class: "data",
					module: "customGame",
					method: "updateSweepstakes",
				},
				payload,
			)
				.then((response) => {
					resolve(response);
				})
				.catch((error) => {
					reject({
						text: error?.text || error?.message || error?.toString() || "Unknown error",
						code: error?.code || 500,
						timestamp: Date.now(),
					});
				});
		});
	}

	addNoty(name, text) {
		let notys = cloneDeep(getRecoil(notyStore));

		let id = notys.data.length === 0 ? 1 : notys.data[notys.data.length - 1].id + 1;
		notys.data.push({ id, name, text });
		setRecoil(notyStore, { ...notys });

		setTimeout(() => {
			this.removeNoty(id);
		}, 5000);
	}

	removeNoty(id) {
		let notys = cloneDeep(getRecoil(notyStore));

		notys.data.forEach((noty, i) => {
			if (noty.id === id) {
				notys.data.splice(i, 1);
				setRecoil(notyStore, { ...notys });
			}
		});
	}

	abortAllRequests() {
		this.activeAbortControllers.forEach((controller) => {
			controller.abort();
			this.activeAbortControllers.delete(controller);
		});
	}

	request(type, method, data) {
		const params = {
			type,
			class: method.class,
			module: method.module,
			method: method.method,
		};

		if (typeof data !== "undefined") params.data = data;

		return new Promise((resolve, reject) => {
			const controller = new AbortController();
			const signal = controller.signal;

			this.activeAbortControllers.add(controller);
			console.log("getRecoil(userStore).token: ", getRecoil(userStore).token);

			fetch(getRecoil(configStore).apiServlet, {
				signal,
				method: "POST",
				headers: {
					Authorization: getRecoil(userStore).token ? "Bearer " + getRecoil(userStore).token : "",
					"X-Project-ID": getRecoil(userStore).brand ? getRecoil(userStore).brand.id : 0,
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					...params,
				}),
			})
				.then(async (response) => {
					this.activeAbortControllers.delete(controller);
					const responseText = await response.text();
					try {
						const responseJson = JSON.parse(responseText);
						return responseJson;
					} catch (error) {
						throw Object.assign(new Error("Invalid JSON response"), {
							status: response.status,
							responseText,
						});
					}
				})
				.then((data) => {
					if (data.success) resolve(data.data);
					else {
						this.addNoty("Error", typeof data.error !== "string" ? JSON.stringify(data.error) : data.error);
						reject({ text: data.error, code: data.code, timestamp: data.timestamp });
					}
				})
				.catch((error) => {
					this.activeAbortControllers.delete(controller);
					if (error.name === "AbortError") {
						reject(error);
						return;
					}

					reject(error);
				});
		});
	}

	welcomeFreespinsSettingsList(payload) {
		return this.request("get", { class: "data", module: "welcomeFreespinsSettings", method: "list" }, payload);
	}

	welcomeFreespinsSettingsListEligibleGames() {
		return this.request("get", { class: "data", module: "welcomeFreespinsSettings", method: "listEligibleGames" }, {});
	}

	welcomeFreespinsSettingsGetCurrent(payload) {
		return this.request("get", { class: "data", module: "welcomeFreespinsSettings", method: "getCurrent" }, payload);
	}

	welcomeFreespinsSettingsGetStatistics(payload) {
		return this.request("get", { class: "data", module: "welcomeFreespinsSettings", method: "getStatistics" }, payload);
	}

	welcomeFreespinsSettingsCreate(payload) {
		return this.request("set", { class: "data", module: "welcomeFreespinsSettings", method: "create" }, payload);
	}

	welcomeFreespinsSettingsUpdatePresentation(payload) {
		return this.request("set", { class: "data", module: "welcomeFreespinsSettings", method: "updatePresentation" }, payload);
	}

	welcomeFreespinsSettingsUpdateIpPolicy(payload) {
		return this.request("set", { class: "data", module: "welcomeFreespinsSettings", method: "updateIpPolicy" }, payload);
	}

	welcomeFreespinsSettingsDisable(payload) {
		return this.request("set", { class: "data", module: "welcomeFreespinsSettings", method: "disable" }, payload);
	}

	loadGamesSection() {
		this.request("get", { class: "data", module: "gamesSection", method: "list" }, { name: "test" })
			.then((list) => {
				setRecoil(gameSectionStore, {
					...getRecoil(gameSectionStore),
					loaded: true,
					error: {
						text: "",
						code: 0,
						timestamp: 0,
					},
					currentSearch: this.currentSearch,
					data: list,
					pagination: { current: 1, total: Math.ceil(list.length / getRecoil(configStore).gameSection.perPage) },
				});
			})
			.catch((error) => {
				if (typeof error.text === "undefined") error = { text: error.toString() };

				setRecoil(gameSectionStore, {
					...getRecoil(gameSectionStore),
					loaded: true,
					error,
				});
			});
	}

	listAvailableGameIds() {
		return this.request("get", { class: "data", module: "gamesSection", method: "listAvailableGameIds" }, {});
	}

	saveGamesOrder(sectionId, expectedOrderedGameIds, orderedGameIds, badgeChanges) {
		const payload = { sectionId, expectedOrderedGameIds, orderedGameIds };
		if (typeof badgeChanges !== "undefined") payload.badgeChanges = badgeChanges;
		return this.request("set", { class: "data", module: "gamesSection", method: "saveGamesOrder" }, payload);
	}

	loadGamesSectionOrderModel() {
		return this.request("get", { class: "data", module: "gamesSection", method: "listOrderModel" }, {});
	}

	saveGamesSectionOrderModel(page, items) {
		return this.request("get", { class: "data", module: "gamesSection", method: "saveOrderModel" }, { page, items });
	}

	deleteGamesSection(id) {
		return this.request("get", { class: "data", module: "gamesSection", method: "delete" }, { id });
	}

	loadBadges() {
		this.request("get", { class: "data", module: "badges", method: "list" }, { name: "test" })
			.then((list) => {
				setRecoil(badgesStore, {
					...getRecoil(badgesStore),
					loaded: true,
					error: {
						text: "",
						code: 0,
						timestamp: 0,
					},
					data: list,
					pagination: { current: 1, total: Math.ceil(list.length / getRecoil(configStore).badges.perPage) },
				});
			})
			.catch((error) => {
				if (typeof error.text === "undefined") error = { text: error.toString() };
				setRecoil(badgesStore, {
					...getRecoil(badgesStore),
					loaded: true,
					error,
				});
			});
	}

	isUserLoaded() {
		return new Promise(function (resolve) {
			(function isUser() {
				if (getRecoil(userStore).level) return resolve();
				setTimeout(isUser, 200);
			})();
		});
	}

	isTopMenuLoaded() {
		return new Promise(function (resolve) {
			(function isTopMenu() {
				if (getRecoil(topMenuStore).length) return resolve();
				setTimeout(isTopMenu, 200);
			})();
		});
	}

	loadBlackListLogWithdrawals(status) {
		setRecoil(blackListLogWithdrawalsStore, { ...getRecoil(blackListLogWithdrawalsStore), data: [], loaded: false });

		const params = {
			searchType: getRecoil(blackListLogWithdrawalsStore).currentSearchType,
			sorting: getRecoil(blackListLogWithdrawalsStore).sorting.sortingColumn,
			page: getRecoil(blackListLogWithdrawalsStore).pagination.current,
			limit: getRecoil(configStore).blackListLogWithdrawals.perPage,
			search: getRecoil(blackListLogWithdrawalsStore).currentSearch,
			status,
		};

		this.request("get", { class: "data", module: "blackListWithdrawals", method: "log/list" }, { ...params })
			.then((list) => {
				setRecoil(blackListLogWithdrawalsStore, {
					...getRecoil(blackListLogWithdrawalsStore),
					loaded: true,
					currentSearch: getRecoil(blackListLogWithdrawalsStore).currentSearch,
					data: list.data,
					pagination: { current: list.page, total: list.totalPages, count: list.total },
					error: {
						text: "",
						code: 0,
						timestamp: 0,
					},
				});
			})
			.catch((e) => {
				setRecoil(blackListLogWithdrawalsStore, { ...getRecoil(blackListLogWithdrawalsStore), loaded: true, error: e });
			});
	}

	loadPlayerBalanceLog() {
		setRecoil(playerBalanceLogStore, { ...getRecoil(playerBalanceLogStore), data: [], loaded: false });

		const params = {
			searchType: getRecoil(playerBalanceLogStore).currentSearchType,
			sorting: getRecoil(playerBalanceLogStore).sorting.sortingColumn,
			page: getRecoil(playerBalanceLogStore).pagination.current,
			limit: getRecoil(configStore).whiteListPlayersWithdrawals.perPage,
			search: getRecoil(playerBalanceLogStore).currentSearch,
		};

		this.request("get", { class: "data", module: "playerBalanceChangeLog", method: "list" }, { ...params })
			.then((list) => {
				setRecoil(playerBalanceLogStore, {
					...getRecoil(playerBalanceLogStore),
					loaded: true,
					currentSearch: getRecoil(playerBalanceLogStore).currentSearch,
					data: list.data,
					pagination: { current: list.page, total: list.totalPages, count: list.total },
					error: {
						text: "",
						code: 0,
						timestamp: 0,
					},
				});
			})
			.catch((e) => {
				setRecoil(playerBalanceLogStore, { ...getRecoil(playerBalanceLogStore), loaded: true, error: e });
			});
	}

	loadUserActionLog(uid) {
		if (!this.isCurrentPlayer(uid)) return Promise.resolve();

		setRecoil(userActionLogStore, { ...getRecoil(userActionLogStore), data: [], loaded: false });

		const params = {
			uid,
			sorting: getRecoil(userActionLogStore).sorting.sortingColumn,
			page: getRecoil(userActionLogStore).pagination.current,
			limit: getRecoil(configStore).userActionLog.perPage,
			search: getRecoil(userActionLogStore).currentSearch,
			searchType: getRecoil(userActionLogStore).currentSearchType,
		};

		this.request("get", { class: "data", module: "logAction", method: "list" }, { ...params })
			.then((list) => {
				if (!this.isCurrentPlayer(uid)) return;
				const payload = list?.data ? list.data : list || {};
				const pagination = payload.pagination || {};
				const currentPage = pagination.page ?? payload.page ?? 1;
				const totalPages = pagination.pages ?? pagination.totalPages ?? payload.totalPages ?? 0;
				const totalCount = pagination.total ?? payload.total ?? 0;
				const items = payload.items || payload.data || [];

				setRecoil(userActionLogStore, {
					...getRecoil(userActionLogStore),
					loaded: true,
					data: items,
					pagination: { current: currentPage, total: totalPages, count: totalCount },
					error: {
						text: "",
						code: 0,
						timestamp: 0,
					},
				});
			})
			.catch((e) => {
				if (!this.isCurrentPlayer(uid) || e?.name === "AbortError") return;
				setRecoil(userActionLogStore, { ...getRecoil(userActionLogStore), loaded: true, error: e });
			});
	}

	loadWhiteListPlayersWithdrawals() {
		setRecoil(whiteListPlayersWithdrawalStore, { ...getRecoil(whiteListPlayersWithdrawalStore), data: [], loaded: false });

		const params = {
			searchType: getRecoil(whiteListPlayersWithdrawalStore).currentSearchType,
			sorting: getRecoil(whiteListPlayersWithdrawalStore).sorting.sortingColumn,
			page: getRecoil(whiteListPlayersWithdrawalStore).pagination.current,
			limit: getRecoil(configStore).whiteListPlayersWithdrawals.perPage,
			search: getRecoil(whiteListPlayersWithdrawalStore).currentSearch,
		};

		this.request("get", { class: "data", module: "whistListPlayersWithdrawal", method: "list" }, { ...params })
			.then((list) => {
				setRecoil(whiteListPlayersWithdrawalStore, {
					...getRecoil(whiteListPlayersWithdrawalStore),
					loaded: true,
					currentSearch: getRecoil(whiteListPlayersWithdrawalStore).currentSearch,
					data: list.data,
					pagination: { current: list.page, total: list.totalPages, count: list.total },
					error: {
						text: "",
						code: 0,
						timestamp: 0,
					},
				});
			})
			.catch((e) => {
				setRecoil(whiteListPlayersWithdrawalStore, { ...getRecoil(whiteListPlayersWithdrawalStore), loaded: true, error: e });
			});
	}

	loadBlackListWithdrawals() {
		setRecoil(blackListWithdrawalsStore, { ...getRecoil(blackListWithdrawalsStore), data: [], loaded: false });

		const params = {
			searchType: getRecoil(blackListWithdrawalsStore).currentSearchType,
			sorting: getRecoil(blackListWithdrawalsStore).sorting.sortingColumn,
			page: getRecoil(blackListWithdrawalsStore).pagination.current,
			limit: getRecoil(configStore).blackListWithdrawals.perPage,
			search: getRecoil(blackListWithdrawalsStore).currentSearch,
		};

		this.request("get", { class: "data", module: "blackListWithdrawals", method: "list" }, { ...params })
			.then((list) => {
				setRecoil(blackListWithdrawalsStore, {
					...getRecoil(blackListWithdrawalsStore),
					loaded: true,
					currentSearch: getRecoil(blackListWithdrawalsStore).currentSearch,
					data: list.data,
					pagination: { current: list.page, total: list.totalPages, count: list.total },
					error: {
						text: "",
						code: 0,
						timestamp: 0,
					},
				});
			})
			.catch((e) => {
				setRecoil(blackListWithdrawalsStore, { ...getRecoil(blackListWithdrawalsStore), loaded: true, error: e });
			});
	}

	loadConfigPromotion() {
		setRecoil(promotionConfigModalStore, {
			...getRecoil(promotionConfigModalStore),
			data: [],
			loaded: false,
			error: {
				text: "",
				code: 0,
				timestamp: 0,
			},
		});

		this.request("get", { class: "data", module: "promotion", method: "getConfig" })
			.then((config) => {
				setRecoil(promotionConfigModalStore, {
					...getRecoil(promotionConfigModalStore),
					loaded: true,
					currentSearch: getRecoil(promotionConfigModalStore).currentSearch,
					fiat: config.fiat.amount,
					crypto: config.crypto.amount,
				});
			})
			.catch((e) => {
				setRecoil(promotionConfigModalStore, { ...getRecoil(promotionConfigModalStore), loaded: true, error: e });
			});
	}

	loadPlayerHidePayway() {
		setRecoil(playerHidePaywayStore, {
			...getRecoil(playerHidePaywayStore),
			data: [],
			loaded: false,
			error: {
				text: "",
				code: 0,
				timestamp: 0,
			},
		});

		const params = {
			searchType: getRecoil(playerHidePaywayStore).currentSearchType,
			sorting: getRecoil(playerHidePaywayStore).sorting.sortingColumn,
			page: getRecoil(playerHidePaywayStore).pagination.current,
			limit: Number(getRecoil(configStore).playerHidePayway.perPage),
			search: getRecoil(playerHidePaywayStore).currentSearch,
		};

		this.request("get", { class: "data", module: "playerHidePayway", method: "list" }, { ...params })
			.then((list) => {
				setRecoil(playerHidePaywayStore, {
					...getRecoil(playerHidePaywayStore),
					loaded: true,
					currentSearch: getRecoil(playerHidePaywayStore).currentSearch,
					data: list.items,
					pagination: { current: list.pagination.page, total: list.pagination.pages, count: list.pagination.total },
				});
			})
			.catch((e) => {
				setRecoil(playerHidePaywayStore, { ...getRecoil(playerHidePaywayStore), loaded: true, error: e });
			});
	}

	loadPlayerBan() {
		setRecoil(playerBanStore, {
			...getRecoil(playerBanStore),
			data: [],
			loaded: false,
			error: {
				text: "",
				code: 0,
				timestamp: 0,
			},
		});

		const params = {
			searchType: getRecoil(playerBanStore).currentSearchType,
			sorting: getRecoil(playerBanStore).sorting.sortingColumn,
			page: getRecoil(playerBanStore).pagination.current,
			limit: Number(getRecoil(configStore).playerBan.perPage),
			search: getRecoil(playerBanStore).currentSearch,
		};

		this.request("get", { class: "data", module: "playerBan", method: "list" }, { ...params })
			.then((list) => {
				setRecoil(playerBanStore, {
					...getRecoil(playerBanStore),
					loaded: true,
					currentSearch: getRecoil(playerBanStore).currentSearch,
					data: list.items,
					pagination: { current: list.pagination.page, total: list.pagination.pages, count: list.pagination.total },
				});
			})
			.catch((e) => {
				setRecoil(playerBanStore, { ...getRecoil(playerBanStore), loaded: true, error: e });
			});
	}

	loadBrandLicenses() {
		setRecoil(brandLicensesStore, {
			...getRecoil(brandLicensesStore),
			data: [],
			loaded: false,
			error: {
				text: "",
				code: 0,
				timestamp: 0,
			},
		});

		const params = {
			searchType: getRecoil(brandLicensesStore).currentSearchType,
			sorting: getRecoil(brandLicensesStore).sorting.sortingColumn,
			page: getRecoil(brandLicensesStore).pagination.current,
			limit: Number(getRecoil(configStore).brandLicenses.perPage),
			search: getRecoil(brandLicensesStore).currentSearch,
		};

		this.request("get", { class: "data", module: "brandLicenses", method: "list" }, { ...params })
			.then((list) => {
				setRecoil(brandLicensesStore, {
					...getRecoil(brandLicensesStore),
					loaded: true,
					currentSearch: getRecoil(brandLicensesStore).currentSearch,
					data: list.items,
					pagination: { current: list.pagination.page, total: list.pagination.pages, count: list.pagination.total },
				});
			})
			.catch((e) => {
				setRecoil(brandLicensesStore, { ...getRecoil(brandLicensesStore), loaded: true, error: e });
			});
	}

	normalizeNotificationSearch(searchType, objectSearch) {
		if (searchType?.value === "date" || (objectSearch && typeof objectSearch === "object")) {
			return objectSearch || "";
		}

		if (typeof objectSearch !== "string") {
			return objectSearch ? String(objectSearch) : "";
		}

		const trimmed = objectSearch.trim();
		if (searchType?.value === "id") {
			return trimmed.replace(/^#/, "").trim();
		}

		return trimmed;
	}

	loadNotifications(searchType, objectSearch, page = null) {
		const requestId = ++this.notificationsListRequestId;
		const store = getRecoil(notificationsStore);
		const nextPage = Number.parseInt(page != null ? page : store.pagination.current, 10);
		const search = this.normalizeNotificationSearch(searchType, objectSearch);

		setRecoil(notificationsStore, {
			...store,
			data: [],
			loaded: false,
			error: {
				text: "",
				code: 0,
				timestamp: 0,
			},
			pagination: {
				...store.pagination,
				current: Number.isInteger(nextPage) && nextPage > 0 ? nextPage : 1,
			},
		});

		const params = {
			page: Number.isInteger(nextPage) && nextPage > 0 ? nextPage : 1,
			limit: Number(getRecoil(configStore).notifications?.perPage || 25),
			search: search ? search : "",
			sorting: getRecoil(notificationsStore).sorting.sortingColumn,
			searchType,
		};

		this.request("get", { class: "data", module: "notification", method: "list" }, params)
			.then((list) => {
				if (requestId !== this.notificationsListRequestId) return;

				const notificationData = Array.isArray(list) ? list : list.items || list.data || [];
				const totalItems = Array.isArray(list) ? list.length : list.pagination?.total || notificationData.length;

				setRecoil(notificationsStore, {
					...getRecoil(notificationsStore),
					loaded: true,
					currentSearch: params.search,
					data: notificationData,
					pagination: {
						current: list.pagination?.page || params.page,
						total: list.pagination?.pages || Math.ceil(totalItems / params.limit),
						count: totalItems,
					},
				});
			})
			.catch((e) => {
				if (requestId !== this.notificationsListRequestId) return;
				setRecoil(notificationsStore, { ...getRecoil(notificationsStore), loaded: true, error: e });
			});
	}

	loadNotificationTags() {
		setRecoil(notificationTagsStore, (prev) => ({ ...prev, error: "", loaded: false }));
		return this.request("get", { class: "data", module: "notification", method: "get" }, {})
			.then((result) => {
				setRecoil(notificationTagsStore, (prev) => ({
					...prev,
					data: Array.isArray(result?.tags) ? result.tags : [],
					error: "",
					loaded: true,
				}));
				return result;
			})
			.catch((error) => {
				setRecoil(notificationTagsStore, (prev) => ({ ...prev, error: error?.text || String(error), loaded: true }));
				throw error;
			});
	}

	loadNotificationQueue(search = null, page = null, dateRange = undefined) {
		const requestId = ++this.notificationQueueRequestId;
		const notifications = getRecoil(notificationsStore);
		const queueState = notifications.queue || {};
		const rawSearch = typeof search === "string" ? search : queueState.currentSearch || "";
		const requestSearch = rawSearch.trim();
		const requestDateRange = dateRange === undefined ? queueState.currentDateRange || null : dateRange;
		const nextPage = Number.parseInt(page != null ? page : queueState.pagination?.current, 10);

		setRecoil(notificationsStore, {
			...notifications,
			queue: {
				...queueState,
				data: [],
				loaded: false,
				error: {
					text: "",
					code: 0,
					timestamp: 0,
				},
			},
		});

		const params = {
			page: Number.isInteger(nextPage) && nextPage > 0 ? nextPage : 1,
			limit: Number(getRecoil(configStore).notifications?.perPage || 25),
			search: requestSearch,
			dateRange: requestDateRange && requestDateRange.type === "absolute" ? requestDateRange : null,
			sorting: queueState.sorting?.sortingColumn || {
				order: "DESC",
				column: "sendDate",
			},
		};

		this.request("get", { class: "data", module: "notification", method: "queue" }, params)
			.then((list) => {
				if (requestId !== this.notificationQueueRequestId) return;

				const queueData = Array.isArray(list) ? list : list.items || list.data || [];
				const totalItems = Array.isArray(list) ? list.length : list.pagination?.total || queueData.length;

				setRecoil(notificationsStore, {
					...getRecoil(notificationsStore),
					queue: {
						...getRecoil(notificationsStore).queue,
						loaded: true,
						currentSearch: params.search,
						currentDateRange: params.dateRange,
						data: queueData,
						pagination: {
							current: list.pagination?.page || params.page,
							total: list.pagination?.pages || Math.ceil(totalItems / params.limit),
							count: totalItems,
						},
					},
				});
			})
			.catch((e) => {
				if (requestId !== this.notificationQueueRequestId) return;
				setRecoil(notificationsStore, {
					...getRecoil(notificationsStore),
					queue: {
						...getRecoil(notificationsStore).queue,
						loaded: true,
						error: e,
					},
				});
			});
	}

	loadPromotion() {
		setRecoil(promotionStore, {
			...getRecoil(promotionStore),
			data: [],
			loaded: false,
			error: {
				text: "",
				code: 0,
				timestamp: 0,
			},
		});

		const params = {
			searchType: getRecoil(promotionStore).currentSearchType,
			sorting: getRecoil(promotionStore).sorting.sortingColumn,
			page: getRecoil(promotionStore).pagination.current,
			limit: Number(getRecoil(configStore).promotion.perPage),
			search: getRecoil(promotionStore).currentSearch,
		};

		this.request("get", { class: "data", module: "promotion", method: "list" }, { ...params })
			.then((list) => {
				setRecoil(promotionStore, {
					...getRecoil(promotionStore),
					loaded: true,
					currentSearch: getRecoil(promotionStore).currentSearch,
					data: list.items,
					pagination: { current: list.pagination.page, total: list.pagination.pages, count: list.pagination.total },
				});
			})
			.catch((e) => {
				setRecoil(promotionStore, { ...getRecoil(promotionStore), loaded: true, error: e });
			});
	}

	loadWalletPromotionList(project) {
		setRecoil(walletPromotionStore, {
			...getRecoil(walletPromotionStore),
			data: [],
			loaded: false,
			error: {
				text: "",
				code: 0,
				timestamp: 0,
			},
		});

		const params = {
			sorting: getRecoil(walletPromotionStore).sorting.sortingColumn,
			page: getRecoil(walletPromotionStore).pagination.current,
			limit: Number(getRecoil(configStore).walletPromotion.perPage),
			search: getRecoil(walletPromotionStore).currentSearch,
			project: typeof project !== "undefined" ? project : 0,
		};

		this.request("get", { class: "data", module: "walletPromotion", method: "list" }, { ...params })
			.then((list) => {
				const listPagination = list?.pagination || {};
				const items = list?.items || list?.data || [];

				setRecoil(walletPromotionStore, {
					...getRecoil(walletPromotionStore),
					loaded: true,
					currentSearch: getRecoil(walletPromotionStore).currentSearch,
					data: items,
					pagination: {
						current: listPagination.page || list.page || params.page,
						total: listPagination.pages || listPagination.totalPages || list.totalPages || 0,
						count: listPagination.total || list.total || items.length,
					},
				});
			})
			.catch((e) => {
				setRecoil(walletPromotionStore, { ...getRecoil(walletPromotionStore), loaded: true, error: e });
			});
	}

	loadWalletGroups(project) {
		setRecoil(walletGroupsStore, {
			...getRecoil(walletGroupsStore),
			data: [],
			loaded: false,
			error: { text: "", code: 0, timestamp: 0 },
		});

		const params = {
			sorting: getRecoil(walletGroupsStore).sorting.sortingColumn,
			page: getRecoil(walletGroupsStore).pagination.current,
			limit: Number(getRecoil(configStore).walletGroups.perPage),
			search: getRecoil(walletGroupsStore).currentSearch,
			project: typeof project !== "undefined" ? project : 0,
		};

		this.request("get", { class: "data", module: "walletGroups", method: "list" }, params)
			.then((list) => {
				const items = list?.items || [];
				const pagination = list?.pagination || {};
				setRecoil(walletGroupsStore, {
					...getRecoil(walletGroupsStore),
					loaded: true,
					data: items,
					currencies: Array.isArray(list?.currencies) ? list.currencies : [],
					pagination: {
						current: pagination.page || params.page,
						total: pagination.pages || 0,
						count: pagination.total || items.length,
					},
				});
			})
			.catch((error) => {
				setRecoil(walletGroupsStore, { ...getRecoil(walletGroupsStore), loaded: true, error });
			});
	}

	loadDepositLadderList(project, archived = false) {
		const targetStore = archived ? depositLadderArchiveStore : depositLadderStore;
		setRecoil(targetStore, { ...getRecoil(targetStore), loaded: false });
		const store = getRecoil(targetStore);
		this.request(
			"get",
			{ class: "data", module: "depositLadder", method: "list" },
			{
				sorting: store.sorting.sortingColumn,
				search: store.currentSearch,
				archived: archived ? 1 : 0,
				page: store.pagination.current,
				limit: Number(getRecoil(configStore).depositLadder.perPage),
				project: typeof project !== "undefined" ? project : 0,
			},
		)
			.then((result) => {
				const pagination = result.pagination || {};
				setRecoil(targetStore, {
					...getRecoil(targetStore),
					data: result.items || [],
					pagination: { current: pagination.page || 1, total: pagination.pages || 0, count: pagination.total || 0 },
					loaded: true,
					error: { text: "", code: 0, timestamp: 0 },
				});
			})
			.catch((error) => setRecoil(targetStore, { ...getRecoil(targetStore), loaded: true, error }));
	}

	loadWelcomeBonusDetails(uid, project) {
		return this.request(
			"get",
			{ class: "data", module: "depositLadder", method: "welcomeBonus/details" },
			{
				uid,
				project: typeof project !== "undefined" ? project : 0,
			},
		);
	}

	getDepositLadderStatisticsPayload() {
		const filters = getRecoil(depositLadderStatisticsStore).filters;

		return {
			date:
				filters.date?.type === "absolute"
					? {
							from: filters.date.startDate,
							to: filters.date.endDate,
					  }
					: null,
			ladderId: filters.selectedLadder?.value || "",
			mode: filters.mode,
			refererUid: filters.mode === "affiliate" ? filters.refererUid : "",
			status: filters.status,
			uid: filters.uid,
		};
	}

	formatDepositLadderStatisticsOption(item) {
		return {
			value: String(item.id),
			label: `${item.name} (#${item.id})`,
			description: `Mode: ${item.mode} · ${Number(item.active) ? "Enabled" : "Disabled"}`,
			data: item,
		};
	}

	loadDepositLadderStatisticsOptions(search = "", project) {
		setRecoil(depositLadderStatisticsStore, {
			...getRecoil(depositLadderStatisticsStore),
			ladderOptionsLoading: true,
		});
		this.request(
			"get",
			{ class: "data", module: "depositLadder", method: "statistics/options" },
			{
				search,
				project: typeof project !== "undefined" ? project : 0,
			},
		)
			.then((items) =>
				setRecoil(depositLadderStatisticsStore, {
					...getRecoil(depositLadderStatisticsStore),
					ladderOptions: (items || []).map((item) => this.formatDepositLadderStatisticsOption(item)),
					ladderOptionsLoading: false,
				}),
			)
			.catch((error) =>
				setRecoil(depositLadderStatisticsStore, {
					...getRecoil(depositLadderStatisticsStore),
					error,
					ladderOptionsLoading: false,
				}),
			);
	}

	loadDepositLadderStatistics(project) {
		setRecoil(depositLadderStatisticsStore, { ...getRecoil(depositLadderStatisticsStore), loaded: false });
		this.request(
			"get",
			{ class: "data", module: "depositLadder", method: "statistics" },
			{
				...this.getDepositLadderStatisticsPayload(),
				project: typeof project !== "undefined" ? project : 0,
			},
		)
			.then((data) =>
				setRecoil(depositLadderStatisticsStore, {
					...getRecoil(depositLadderStatisticsStore),
					data: data || { byLadder: [], byStep: [], summary: null },
					loaded: true,
					error: { text: "", code: 0, timestamp: 0 },
				}),
			)
			.catch((error) => setRecoil(depositLadderStatisticsStore, { ...getRecoil(depositLadderStatisticsStore), loaded: true, error }));
	}

	loadDepositLadderStatisticsUsers(project) {
		setRecoil(depositLadderStatisticsUsersStore, { ...getRecoil(depositLadderStatisticsUsersStore), loaded: false });
		const store = getRecoil(depositLadderStatisticsUsersStore);
		this.request(
			"get",
			{ class: "data", module: "depositLadder", method: "statistics/users" },
			{
				...this.getDepositLadderStatisticsPayload(),
				page: store.pagination.current,
				limit: Number(getRecoil(configStore).depositLadder.perPage),
				project: typeof project !== "undefined" ? project : 0,
			},
		)
			.then((result) => {
				const pagination = result.pagination || {};
				setRecoil(depositLadderStatisticsUsersStore, {
					...getRecoil(depositLadderStatisticsUsersStore),
					data: result.items || [],
					pagination: { current: pagination.page || 1, total: pagination.pages || 0, count: pagination.total || 0 },
					loaded: true,
					error: { text: "", code: 0, timestamp: 0 },
				});
			})
			.catch((error) => setRecoil(depositLadderStatisticsUsersStore, { ...getRecoil(depositLadderStatisticsUsersStore), loaded: true, error }));
	}

	loadTgBotsList(project) {
		setRecoil(tgBotsStore, {
			...getRecoil(tgBotsStore),
			data: [],
			loaded: false,
			error: {
				text: "",
				code: 0,
				timestamp: 0,
			},
		});

		const params = {
			sorting: getRecoil(tgBotsStore).sorting.sortingColumn,
			page: getRecoil(tgBotsStore).pagination.current,
			limit: Number(getRecoil(configStore).tgBots.perPage),
			search: getRecoil(tgBotsStore).currentSearch,
			project: typeof project !== "undefined" ? project : 0,
		};

		this.request("get", { class: "data", module: "telegramBots", method: "list" }, { ...params })
			.then((list) => {
				const listPagination = list?.pagination || {};
				const items = list?.items || list?.data || [];

				setRecoil(tgBotsStore, {
					...getRecoil(tgBotsStore),
					loaded: true,
					currentSearch: getRecoil(tgBotsStore).currentSearch,
					data: items,
					pagination: {
						current: listPagination.page || list.page || params.page,
						total: listPagination.pages || listPagination.totalPages || list.totalPages || 0,
						count: listPagination.total || list.total || items.length,
					},
				});
			})
			.catch((e) => {
				setRecoil(tgBotsStore, { ...getRecoil(tgBotsStore), loaded: true, error: e });
			});
	}

	loadTgBotsRotation() {
		const current = getRecoil(tgBotsRotationStore);
		setRecoil(tgBotsRotationStore, {
			...current,
			loaded: false,
			error: {
				text: "",
				code: 0,
				timestamp: 0,
			},
		});

		Promise.all([this.request("get", { class: "data", module: "telegramBots", method: "getRotation" }, {}), this.request("get", { class: "data", module: "telegramBots", method: "listEligible" }, {})])
			.then(([rotation, eligible]) => {
				const items = eligible?.items || eligible?.data || [];
				setRecoil(tgBotsRotationStore, {
					...getRecoil(tgBotsRotationStore),
					loaded: true,
					saving: false,
					enabled: Boolean(rotation?.enabled),
					botIds: Array.isArray(rotation?.botIds) ? rotation.botIds.map((id) => Number(id)) : [],
					intervalDays: String(rotation?.intervalDays ?? 14),
					nextRotationAt: rotation?.nextRotationAt || null,
					eligibleBots: items,
					error: {
						text: "",
						code: 0,
						timestamp: 0,
					},
				});
			})
			.catch((error) => {
				setRecoil(tgBotsRotationStore, {
					...createTgBotsRotationState(),
					...getRecoil(tgBotsRotationStore),
					loaded: true,
					saving: false,
					error,
				});
			});
	}

	saveTgBotsRotation(payload) {
		const current = getRecoil(tgBotsRotationStore);
		setRecoil(tgBotsRotationStore, { ...current, saving: true });

		return this.request("set", { class: "data", module: "telegramBots", method: "editRotation" }, payload)
			.then((rotation) => {
				setRecoil(tgBotsRotationStore, {
					...getRecoil(tgBotsRotationStore),
					saving: false,
					enabled: Boolean(rotation?.enabled),
					botIds: Array.isArray(rotation?.botIds) ? rotation.botIds.map((id) => Number(id)) : payload.botIds,
					intervalDays: String(rotation?.intervalDays ?? payload.intervalDays),
					nextRotationAt: rotation?.nextRotationAt || null,
				});
				this.loadTgBotsList();
				this.loadTgBotsRotation();
				return rotation;
			})
			.catch((error) => {
				setRecoil(tgBotsRotationStore, { ...getRecoil(tgBotsRotationStore), saving: false });
				throw error;
			});
	}

	loadWebhookBroadcasts() {
		setRecoil(webhookBroadcastsStore, {
			...getRecoil(webhookBroadcastsStore),
			data: [],
			loaded: false,
			error: {
				text: "",
				code: 0,
				timestamp: 0,
			},
		});

		const params = {
			sorting: getRecoil(webhookBroadcastsStore).sorting.sortingColumn,
			page: getRecoil(webhookBroadcastsStore).pagination.current,
			limit: Number(getRecoil(configStore).webhookBroadcasts.perPage),
			search: getRecoil(webhookBroadcastsStore).currentSearch,
		};

		this.request("get", { class: "data", module: "webhookBroadcast", method: "list" }, { ...params })
			.then((list) => {
				const listPagination = list?.pagination || {};
				const items = Array.isArray(list) ? list : list?.items || list?.data || [];

				setRecoil(webhookBroadcastsStore, {
					...getRecoil(webhookBroadcastsStore),
					loaded: true,
					currentSearch: getRecoil(webhookBroadcastsStore).currentSearch,
					data: items,
					pagination: {
						current: listPagination.page || list.page || params.page,
						total: listPagination.pages || listPagination.totalPages || list.totalPages || 0,
						count: listPagination.total || list.total || items.length,
					},
				});
			})
			.catch((e) => {
				setRecoil(webhookBroadcastsStore, { ...getRecoil(webhookBroadcastsStore), loaded: true, error: e });
			});
	}

	loadWebhookPresets() {
		setRecoil(webhookPresetsStore, {
			...getRecoil(webhookPresetsStore),
			data: [],
			loaded: false,
			error: {
				text: "",
				code: 0,
				timestamp: 0,
			},
		});

		const params = {
			sorting: getRecoil(webhookPresetsStore).sorting.sortingColumn,
			page: getRecoil(webhookPresetsStore).pagination.current,
			limit: Number(getRecoil(configStore).webhookPresets.perPage),
			search: getRecoil(webhookPresetsStore).currentSearch,
		};

		this.request("get", { class: "data", module: "webhookPreset", method: "list" }, { ...params })
			.then((list) => {
				const listPagination = list?.pagination || {};
				const items = Array.isArray(list) ? list : list?.items || list?.data || [];

				setRecoil(webhookPresetsStore, {
					...getRecoil(webhookPresetsStore),
					loaded: true,
					currentSearch: getRecoil(webhookPresetsStore).currentSearch,
					data: items,
					pagination: {
						current: listPagination.page || list.page || params.page,
						total: listPagination.pages || listPagination.totalPages || list.totalPages || 0,
						count: listPagination.total || list.total || items.length,
					},
				});
			})
			.catch((e) => {
				setRecoil(webhookPresetsStore, { ...getRecoil(webhookPresetsStore), loaded: true, error: e });
			});
	}

	loadClients() {
		setRecoil(clientStore, {
			...getRecoil(clientStore),
			data: [],
			loaded: false,
			error: {
				text: "",
				code: 0,
				timestamp: 0,
			},
		});

		const params = {
			searchType: getRecoil(clientStore).currentSearchType,
			sorting: getRecoil(clientStore).sorting.sortingColumn,
			page: getRecoil(clientStore).pagination.current,
			limit: Number(getRecoil(configStore).client.perPage),
			search: getRecoil(clientStore).currentSearch,
		};

		this.request("get", { class: "data", module: "clients", method: "list" }, { ...params })
			.then((list) => {
				setRecoil(clientStore, {
					...getRecoil(clientStore),
					loaded: true,
					currentSearch: getRecoil(clientStore).currentSearch,
					data: list.items,
					pagination: { current: list.pagination.page, total: list.pagination.pages, count: list.pagination.total },
				});
			})
			.catch((e) => {
				setRecoil(clientStore, { ...getRecoil(clientStore), loaded: true, error: e });
			});
	}

	loadProviderLog() {
		setRecoil(providerLogStore, {
			...getRecoil(providerLogStore),
			data: [],
			loaded: false,
			error: {
				text: "",
				code: 0,
				timestamp: 0,
			},
		});

		const params = {
			searchType: getRecoil(providerLogStore).currentSearchType,
			sorting: getRecoil(providerLogStore).sorting.sortingColumn,
			page: getRecoil(providerLogStore).pagination.current,
			limit: 50,
			search: getRecoil(providerLogStore).currentSearch,
		};

		this.request("get", { class: "data", module: "providerLog", method: "log" }, { ...params })
			.then((list) => {
				setRecoil(providerLogStore, {
					...getRecoil(providerLogStore),
					loaded: true,
					currentSearch: getRecoil(providerLogStore).currentSearch,
					data: list,
				});
			})
			.catch((e) => {
				setRecoil(providerLogStore, { ...getRecoil(providerLogStore), loaded: true, error: e });
			});
	}

	loadSchema() {
		return new Promise((resolve, reject) => {
			this.request("get", { class: "panel", module: "schema", method: "list" }, {})
				.then((list) => {
					return resolve(list);
				})
				.catch((e) => {
					return reject(e);
				});
		});
	}

	loadCurrentSchema() {
		return new Promise((resolve, reject) => {
			this.request("get", { class: "panel", module: "schema", method: "getCurrent" }, {})
				.then((list) => {
					return resolve(list);
				})
				.catch((e) => {
					return reject(e);
				});
		});
	}

	setSchema(schemaName) {
		return new Promise((resolve, reject) => {
			this.request("set", { class: "panel", module: "schema", method: "set" }, { schemaName })
				.then((list) => {
					return resolve(list);
				})
				.catch((e) => {
					return reject(e);
				});
		});
	}

	loadGroup() {
		setRecoil(groupStore, {
			...getRecoil(groupStore),
			data: [],
			loaded: false,
			error: {
				text: "",
				code: 0,
				timestamp: 0,
			},
		});

		const params = {
			searchType: getRecoil(groupStore).currentSearchType,
			sorting: getRecoil(groupStore).sorting.sortingColumn,
			page: getRecoil(groupStore).pagination.current,
			limit: Number(getRecoil(configStore).group.perPage),
			search: getRecoil(groupStore).currentSearch,
		};

		this.request("get", { class: "panel", module: "group", method: "list" }, { ...params })
			.then((list) => {
				setRecoil(groupStore, {
					...getRecoil(groupStore),
					loaded: true,
					currentSearch: getRecoil(groupStore).currentSearch,
					data: list.items,
					pagination: { current: list.pagination.page, total: list.pagination.pages, count: list.pagination.total },
				});
			})
			.catch((e) => {
				setRecoil(groupStore, { ...getRecoil(groupStore), loaded: true, error: e });
			});
	}

	loadPlayerRulesAutomaticLog() {
		setRecoil(playerRulesAutomaticLogStore, {
			...getRecoil(playerRulesAutomaticLogStore),
			data: [],
			loaded: false,
			error: {
				text: "",
				code: 0,
				timestamp: 0,
			},
		});

		const params = {
			searchType: getRecoil(playerRulesAutomaticLogStore).currentSearchType,
			sorting: getRecoil(playerRulesAutomaticLogStore).sorting.sortingColumn,
			page: getRecoil(playerRulesAutomaticLogStore).pagination.current,
			limit: Number(getRecoil(configStore).playerRulesAutomaticLog.perPage),
			search: getRecoil(playerRulesAutomaticLogStore).currentSearch,
		};

		this.request("get", { class: "panel", module: "playerTagAutomaticLog", method: "list" }, { ...params })
			.then((list) => {
				setRecoil(playerRulesAutomaticLogStore, {
					...getRecoil(playerRulesAutomaticLogStore),
					loaded: true,
					currentSearch: getRecoil(playerRulesAutomaticLogStore).currentSearch,
					data: list.items,
					pagination: { current: list.pagination.page, total: list.pagination.pages, count: list.pagination.total },
				});
			})
			.catch((e) => {
				setRecoil(playerRulesAutomaticLogStore, { ...getRecoil(playerRulesAutomaticLogStore), loaded: true, error: e });
			});
	}

	loadProvider() {
		setRecoil(providerOptionsStore, null);
		setRecoil(providerStore, {
			...getRecoil(providerStore),
			data: [],
			loaded: false,
			error: {
				text: "",
				code: 0,
				timestamp: 0,
			},
		});

		const params = {
			searchType: getRecoil(providerStore).currentSearchType,
			sorting: getRecoil(providerStore).sorting.sortingColumn,
			page: getRecoil(providerStore).pagination.current,
			limit: Number(getRecoil(configStore).provider.perPage),
			search: getRecoil(providerStore).currentSearch,
		};

		this.request("get", { class: "data", module: "providers", method: "list" }, { ...params })
			.then((list) => {
				setRecoil(providerOptionsStore, list.options);
				setRecoil(providerStore, {
					...getRecoil(providerStore),
					loaded: true,
					currentSearch: getRecoil(providerStore).currentSearch,
					data: list.items,
					pagination: { current: list.pagination.page, total: list.pagination.pages, count: list.pagination.total },
				});
			})
			.catch((e) => {
				setRecoil(providerOptionsStore, null);
				setRecoil(providerStore, { ...getRecoil(providerStore), loaded: true, error: e });
			});
	}

	getProviderDepositRestriction() {
		return this.request("get", { class: "data", module: "providers", method: "getDepositRestriction" }, {});
	}

	loadProviderOptions(search = "", ids = []) {
		return this.request("get", { class: "data", module: "providers", method: "getDepositRestrictionProviders" }, { search: String(search).trim(), ids });
	}

	saveProviderDepositRestriction(config) {
		return this.request("set", { class: "data", module: "providers", method: "saveDepositRestriction" }, config);
	}

	loadBonuses() {
		setRecoil(bonusesStore, { ...getRecoil(bonusesStore), data: [], loaded: false });

		const params = {
			searchType: getRecoil(bonusesStore).currentSearchType,
			sorting: getRecoil(bonusesStore).sorting.sortingColumn,
			page: getRecoil(bonusesStore).pagination.current,
			limit: getRecoil(configStore).bonuses.perPage,
			search: getRecoil(bonusesStore).currentSearch,
		};

		this.request("get", { class: "data", module: "bonuses", method: "list" }, { ...params })
			.then((list) => {
				list.data.forEach((item) => {
					item.filtered = true;
				});

				setRecoil(bonusesStore, {
					...getRecoil(bonusesStore),
					loaded: true,
					currentSearch: getRecoil(bonusesStore).currentSearch,
					data: list.data,
					pagination: { current: list.page, total: list.totalPages, count: list.total },
				});
			})
			.catch((e) => {
				setRecoil(bonusesStore, { ...getRecoil(bonusesStore), loaded: true, error: e });
			});
	}

	isPaymentDataLoaded() {
		return new Promise(function (resolve) {
			(function isPaymentData() {
				if (getRecoil(loaderStore).paymentData) return resolve();
				setTimeout(isPaymentData, 200);
			})();
		});
	}

	loadStaffSessionDetail(token, brand) {
		setRecoil(staffDetailLogStore, { ...getRecoil(staffDetailLogStore), loaded: false, data: [] });

		this.request("get", { class: "data", module: "staff", method: "sessionDetail" }, { token, brand })
			.then((list) => {
				setRecoil(staffDetailLogStore, { ...getRecoil(staffDetailLogStore), loaded: true, data: list });
			})
			.catch((e) => {
				setRecoil(staffDetailLogStore, { ...getRecoil(staffDetailLogStore), loaded: true, error: e });
			});
	}

	scrollTrack(uid) {
		this.request("get", { class: "users", module: "data", method: "info/scroll" }, { uid });
	}

	isCurrentPlayer(uid, project) {
		const playerPage = getRecoil(playerPageStore);
		if (!playerPage.show || playerPage.uid !== uid) return false;
		if (typeof project === "undefined") return true;
		return String(playerPage.data?.project || 0) === String(project || 0);
	}

	loadPayments(uid, type, project) {
		setRecoil(paymentStore, { ...getRecoil(paymentStore), currentSearch: "", show: true, loaded: false, type, project });
		setRecoil(playerPageStore, { ...getRecoil(playerPageStore), uid });

		this.request(
			"get",
			{ class: "users", module: "data", method: "payments" },
			{
				searchType: type === 1 ? getRecoil(paymentStore).search.deposit.currentSearchType : getRecoil(paymentStore).search.withdrawals.currentSearchType,
				search: type === 1 ? getRecoil(paymentStore).search.deposit.currentSearch : getRecoil(paymentStore).search.withdrawals.currentSearch,
				type,
				sorting: type === 1 ? getRecoil(paymentStore).depositSort.sortingColumn : getRecoil(paymentStore).withdrawalsSort.sortingColumn,
				page: type === 1 ? getRecoil(paymentStore).pagination.deposit.page : getRecoil(paymentStore).pagination.withdrawals.page,
				perPage: getRecoil(configStore).players.payments.perPage,
				uid,
				project,
			},
		)
			.then((list) => {
				if (type === 1) {
					setRecoil(paymentStore, { ...getRecoil(paymentStore), loaded: true, ...list, data: list.deposits, pagination: { ...getRecoil(paymentStore).pagination, deposit: { current: list.pagination.deposits.page, total: list.pagination.deposits.total, totalPage: list.pagination.deposits.totalPage } } });
				} else {
					setRecoil(paymentStore, { ...getRecoil(paymentStore), loaded: true, ...list, data: list.withdrawals, pagination: { ...getRecoil(paymentStore).pagination, withdrawals: { current: list.pagination.withdrawals.page, total: list.pagination.withdrawals.total, totalPage: list.pagination.withdrawals.totalPage } } });
				}
			})
			.catch((e) => {
				setRecoil(paymentStore, { ...getRecoil(paymentStore), loaded: true, error: e });
			});
	}

	loadInfoFortuneWheel(wheelId) {
		setRecoil(fortuneWheelModalStore, { ...getRecoil(fortuneWheelModalStore), loaded: false, openAsChildren: true, wheelId, show: true });

		this.request("get", { class: "users", module: "fortuneWheel", method: "get" }, { wheelId, sorting: getRecoil(fortuneWheelModalStore).sorting.sortingColumn, search: getRecoil(fortuneWheelModalStore).currentSearch, page: getRecoil(fortuneWheelModalStore).pagination.current, limit: getRecoil(configStore).fortuneWheelModal.perPage, project: 0 })
			.then((fortune) => {
				setRecoil(fortuneWheelModalStore, {
					...getRecoil(fortuneWheelModalStore),
					...getRecoil(fortuneWheelModalStore),
					openAsChildren: true,
					pagination: {
						current: fortune.page,
						total: fortune.totalPages,
						count: fortune.total,
					},
					error: {
						text: "",
						code: 0,
						timestamp: 0,
					},
					data: fortune.data,
					loaded: true,
				});
			})
			.catch((e) => {
				console.log(e);
				setRecoil(fortuneWheelModalStore, { ...getRecoil(fortuneWheelModalStore), ...getRecoil(fortuneWheelModalStore), loaded: true, error: e });
			});
	}

	loadPanelPaymentsUsers() {
		setRecoil(panelPaymentsUsersStore, {
			...getRecoil(panelPaymentsUsersStore),
			loaded: false,
			error: {
				text: "",
				code: 0,
				timestamp: 0,
			},
			data: [],
			currentSearch: "",
		});

		this.request(
			"get",
			{
				class: "panel",
				module: "payments",
				method: "users",
			},
			{},
		)
			.then((users) => {
				setRecoil(panelPaymentsUsersStore, {
					...getRecoil(panelPaymentsUsersStore),
					data: users,
					pagination: { current: 1, total: Math.ceil(users.length / getRecoil(configStore).panelPaymentsUsers.perPage) },
					error: {
						text: "",
						code: 0,
						timestamp: 0,
					},
					loaded: true,
				});
			})
			.catch((e) => {
				setRecoil(panelPaymentsUsersStore, { ...getRecoil(panelPaymentsUsersStore), loaded: true, error: e });
			});
	}

	loadPanelPaymentsLog(login, date) {
		setRecoil(panelPaymentsLogStore, {
			...getRecoil(panelPaymentsLogStore),
			loaded: false,
			error: {
				text: "",
				code: 0,
				timestamp: 0,
			},
			data: [],
			currentSearch: "",
		});

		// The log is built by a server task; it is started once and then polled by taskId.
		const requestId = (this.panelPaymentsLogRequestId || 0) + 1;
		this.panelPaymentsLogRequestId = requestId;
		const isCurrent = () => requestId === this.panelPaymentsLogRequestId;
		const requestLog = (payload) =>
			this.request(
				"get",
				{
					class: "panel",
					module: "payments",
					method: "log",
				},
				payload,
			);
		const getTaskError = (text) => ({ text, code: 0, timestamp: Date.now() });
		const pollTask = (taskId) =>
			requestLog({ taskId }).then((task) => {
				if (!isCurrent()) return null;
				if (task?.status === "completed") return task.response;
				if (task?.status === "failed") throw getTaskError(task.error || "Couldn't load payments log");

				return new Promise((resolve) => setTimeout(resolve, PANEL_PAYMENTS_LOG_POLL_MS)).then(() => (isCurrent() ? pollTask(taskId) : null));
			});

		requestLog({ login, date })
			.then((task) => {
				if (!task?.taskId) throw getTaskError(task?.status || "Couldn't start loading payments log");

				return pollTask(task.taskId);
			})
			.then((staffLog) => {
				if (!staffLog || !isCurrent()) return;

				setRecoil(panelPaymentsLogStore, {
					...getRecoil(panelPaymentsLogStore),
					data: staffLog.data,
					pagination: { current: 1, total: Math.ceil(staffLog.data.length / getRecoil(configStore).panelPaymentsLogStore.perPage) },
					error: {
						text: "",
						code: 0,
						timestamp: 0,
					},
					loaded: true,
				});
			})
			.catch((e) => {
				if (!isCurrent()) return;
				setRecoil(panelPaymentsLogStore, { ...getRecoil(panelPaymentsLogStore), loaded: true, error: e });
			});
	}

	loadInfo(uid, project) {
		const requestId = ++this.playerInfoRequestId;
		const normalizedProject = typeof project !== "undefined" ? project : 0;

		setRecoil(playerPageStore, {
			...cloneDeep(playerPageDefaultState),
			loaded: false,
			show: true,
			uid,
		});

		return this.request("get", { class: "users", module: "data", method: "info" }, { uid, light: false, project: normalizedProject })
			.then((data) => {
				const currentPlayerPage = getRecoil(playerPageStore);
				if (requestId !== this.playerInfoRequestId || !currentPlayerPage.show || currentPlayerPage.uid !== uid) return;

				setRecoil(playerPageStore, { ...currentPlayerPage, data: { ...data, project: normalizedProject, initialPromocodes: data.promoCodes, initialRegData: data.regdata, initialUserStats: data.userStats }, loaded: true });
				setTimeout(() => {
					const latestPlayerPage = getRecoil(playerPageStore);
					if (requestId !== this.playerInfoRequestId || !latestPlayerPage.show || latestPlayerPage.uid !== uid || String(latestPlayerPage.data?.project || 0) !== String(normalizedProject)) return;
					this.loadLog(uid, normalizedProject);
				}, 250);
			})
			.catch((e) => {
				const currentPlayerPage = getRecoil(playerPageStore);
				if (e?.name === "AbortError" || requestId !== this.playerInfoRequestId || !currentPlayerPage.show || currentPlayerPage.uid !== uid) return;
				setRecoil(playerPageStore, { ...currentPlayerPage, loaded: true, error: e });
			});
	}

	loadSweepStakes(uid, project, status = "active") {
		if (!this.isCurrentPlayer(uid, project)) return Promise.resolve();

		const storeKey = status === "history" ? "sweepstakesHistory" : "sweepstakesActive";
		const configKey = status === "history" ? "sweepstakesHistory" : "sweepstakesActive";

		setRecoil(playerPageStore, {
			...getRecoil(playerPageStore),
			uid,
			bonuses: {
				...getRecoil(playerPageStore).bonuses,
				[storeKey]: {
					...getRecoil(playerPageStore).bonuses[storeKey],
					loaded: false,
				},
			},
		});

		this.request(
			"get",
			{ class: "data", module: "prize", method: "history" },
			{
				uid,
				status,
				searchType: getRecoil(playerPageStore).bonuses[storeKey].currentSearchType,
				sorting: getRecoil(playerPageStore).bonuses[storeKey].sorting.sortingColumn,
				search: getRecoil(playerPageStore).bonuses[storeKey].currentSearch,
				page: getRecoil(playerPageStore).bonuses[storeKey].pagination.current,
				limit: getRecoil(configStore).players.bonuses[configKey].perPage,
				project: typeof project !== "undefined" ? project : 0,
			},
		)
			.then((sweepstakes) => {
				if (!this.isCurrentPlayer(uid, project)) return;
				setRecoil(playerPageStore, {
					...getRecoil(playerPageStore),
					uid,
					bonuses: {
						...getRecoil(playerPageStore).bonuses,
						[storeKey]: {
							...getRecoil(playerPageStore).bonuses[storeKey],
							pagination: {
								current: sweepstakes.pagination.page,
								total: sweepstakes.pagination.totalPages,
								count: sweepstakes.pagination.total,
							},
							data: sweepstakes.data,
							loaded: true,
						},
					},
				});
			})
			.catch((e) => {
				if (!this.isCurrentPlayer(uid, project) || e?.name === "AbortError") return;
				console.log(e);
				setRecoil(playerPageStore, {
					...getRecoil(playerPageStore),
					uid,
					bonuses: {
						...getRecoil(playerPageStore).bonuses,
						[storeKey]: {
							...getRecoil(playerPageStore).bonuses[storeKey],
							pagination: {
								current: 0,
								total: 0,
								count: 0,
							},
							data: [],
							loaded: true,
							error: e,
						},
					},
				});
			});
	}

	loadAchievements(uid, project, status = "active") {
		if (!this.isCurrentPlayer(uid, project)) return Promise.resolve();

		const storeKey = status === "history" ? "achievementsHistory" : "achievementsActive";
		const configKey = status === "history" ? "achievementsHistory" : "achievementsActive";

		setRecoil(playerPageStore, {
			...getRecoil(playerPageStore),
			uid,
			bonuses: {
				...getRecoil(playerPageStore).bonuses,
				[storeKey]: {
					...getRecoil(playerPageStore).bonuses[storeKey],
					loaded: false,
				},
			},
		});

		this.request(
			"get",
			{ class: "data", module: "achievement", method: "history" },
			{
				uid,
				status,
				searchType: getRecoil(playerPageStore).bonuses[storeKey].currentSearchType,
				sorting: getRecoil(playerPageStore).bonuses[storeKey].sorting.sortingColumn,
				search: getRecoil(playerPageStore).bonuses[storeKey].currentSearch,
				page: getRecoil(playerPageStore).bonuses[storeKey].pagination.current,
				limit: getRecoil(configStore).players.bonuses[configKey].perPage,
				project: typeof project !== "undefined" ? project : 0,
			},
		)
			.then((achievements) => {
				if (!this.isCurrentPlayer(uid, project)) return;
				setRecoil(playerPageStore, {
					...getRecoil(playerPageStore),
					uid,
					bonuses: {
						...getRecoil(playerPageStore).bonuses,
						[storeKey]: {
							...getRecoil(playerPageStore).bonuses[storeKey],
							pagination: {
								current: achievements.pagination.page,
								total: achievements.pagination.totalPages,
								count: achievements.pagination.total,
							},
							data: achievements.data,
							loaded: true,
						},
					},
				});
			})
			.catch((e) => {
				if (!this.isCurrentPlayer(uid, project) || e?.name === "AbortError") return;
				console.log(e);
				setRecoil(playerPageStore, {
					...getRecoil(playerPageStore),
					uid,
					bonuses: {
						...getRecoil(playerPageStore).bonuses,
						[storeKey]: {
							...getRecoil(playerPageStore).bonuses[storeKey],
							pagination: {
								current: 0,
								total: 0,
								count: 0,
							},
							data: [],
							loaded: true,
							error: e,
						},
					},
				});
			});
	}
	loadAllUserPromo(uid, project) {
		if (!this.isCurrentPlayer(uid, project)) return Promise.resolve();

		setRecoil(playerPageStore, {
			...getRecoil(playerPageStore),
			uid,
			bonuses: {
				...getRecoil(playerPageStore).bonuses,
				allPromocode: {
					...getRecoil(playerPageStore).bonuses.allPromocode,
					loaded: false,
				},
			},
		});

		this.request(
			"get",
			{ class: "data", module: "bonuses", method: "get" },
			{ uid, searchType: getRecoil(playerPageStore).bonuses.allPromocode.currentSearchType, sorting: getRecoil(playerPageStore).bonuses.allPromocode.sorting.sortingColumn, search: getRecoil(playerPageStore).bonuses.allPromocode.currentSearch, page: getRecoil(playerPageStore).bonuses.allPromocode.pagination.current, limit: getRecoil(configStore).players.bonuses.allPromocode.perPage, project: typeof project !== "undefined" ? project : 0 },
		)
			.then((promocode) => {
				if (!this.isCurrentPlayer(uid, project)) return;
				setRecoil(playerPageStore, {
					...getRecoil(playerPageStore),
					uid,
					bonuses: {
						...getRecoil(playerPageStore).bonuses,
						allPromocode: {
							...getRecoil(playerPageStore).bonuses.allPromocode,
							pagination: {
								current: promocode.page,
								total: promocode.totalPages,
								count: promocode.total,
							},
							data: promocode.data,
							loaded: true,
						},
					},
				});
			})
			.catch((e) => {
				if (!this.isCurrentPlayer(uid, project) || e?.name === "AbortError") return;
				console.log(e);
				setRecoil(playerPageStore, {
					...getRecoil(playerPageStore),
					uid,
					bonuses: {
						...getRecoil(playerPageStore).bonuses,
						allPromocode: {
							...getRecoil(playerPageStore).bonuses.allPromocode,
							pagination: {
								current: 0,
								total: 0,
								count: 0,
							},
							data: [],
							loaded: true,
							error: e,
						},
					},
				});
			});
	}

	loadUserPromocodes(uid, project) {
		if (!this.isCurrentPlayer(uid, project)) return Promise.resolve();

		setRecoil(playerPageStore, {
			...getRecoil(playerPageStore),
			uid,
			bonuses: {
				...getRecoil(playerPageStore).bonuses,
				promocode: {
					...getRecoil(playerPageStore).bonuses.promocode,
					loaded: false,
				},
			},
		});

		this.request("get", { class: "users", module: "promo", method: "list" }, { uid, sorting: getRecoil(playerPageStore).bonuses.promocode.sorting.sortingColumn, search: getRecoil(playerPageStore).bonuses.promocode.currentSearch, page: getRecoil(playerPageStore).bonuses.promocode.pagination.current, limit: getRecoil(configStore).players.bonuses.promocode.perPage, project: typeof project !== "undefined" ? project : 0 })
			.then((promocode) => {
				if (!this.isCurrentPlayer(uid, project)) return;
				setRecoil(playerPageStore, {
					...getRecoil(playerPageStore),
					uid,
					bonuses: {
						...getRecoil(playerPageStore).bonuses,
						promocode: {
							...getRecoil(playerPageStore).bonuses.promocode,
							pagination: {
								current: promocode.page,
								total: promocode.totalPages,
								count: promocode.total,
							},
							data: promocode.data,
							loaded: true,
						},
					},
				});
			})
			.catch((e) => {
				if (!this.isCurrentPlayer(uid, project) || e?.name === "AbortError") return;
				console.log(e);
				setRecoil(playerPageStore, {
					...getRecoil(playerPageStore),
					uid,
					bonuses: {
						...getRecoil(playerPageStore).bonuses,
						promocode: {
							...getRecoil(playerPageStore).bonuses.promocode,
							loaded: true,
							error: e,
						},
					},
				});
			});
	}

	loadUserRain(uid, project) {
		if (!this.isCurrentPlayer(uid, project)) return Promise.resolve();

		setRecoil(playerPageStore, {
			...getRecoil(playerPageStore),
			uid,
			bonuses: {
				...getRecoil(playerPageStore).bonuses,
				rain: {
					...getRecoil(playerPageStore).bonuses.rain,
					loaded: false,
				},
			},
		});

		this.request("get", { class: "users", module: "rain", method: "list" }, { uid, sorting: getRecoil(playerPageStore).bonuses.rain.sorting.sortingColumn, search: getRecoil(playerPageStore).bonuses.rain.currentSearch, page: getRecoil(playerPageStore).bonuses.rain.pagination.current, limit: getRecoil(configStore).players.bonuses.rain.perPage, project: typeof project !== "undefined" ? project : 0 })
			.then((rain) => {
				if (!this.isCurrentPlayer(uid, project)) return;
				setRecoil(playerPageStore, {
					...getRecoil(playerPageStore),
					uid,
					bonuses: {
						...getRecoil(playerPageStore).bonuses,
						rain: {
							...getRecoil(playerPageStore).bonuses.rain,
							pagination: {
								current: rain.page,
								total: rain.totalPages,
								count: rain.total,
							},
							data: rain.data,
							loaded: true,
						},
					},
				});
			})
			.catch((e) => {
				if (!this.isCurrentPlayer(uid, project) || e?.name === "AbortError") return;
				console.log(e);
				setRecoil(playerPageStore, {
					...getRecoil(playerPageStore),
					uid,
					bonuses: {
						...getRecoil(playerPageStore).bonuses,
						rain: {
							...getRecoil(playerPageStore).bonuses.rain,
							loaded: true,
							error: e,
						},
					},
				});
			});
	}

	loadUserPartnerSettingsList(uid, limit = 1, page = 1) {
		const requestId = ++this.partnerSettingsRequestId;

		setRecoil(partnerSettingsStore, {
			...getRecoil(partnerSettingsStore),
			loaded: false,
		});

		return new Promise((resolve, reject) => {
			this.request(
				"get",
				{ class: "data", module: "partnerSettings", method: "list" },
				{
					uid,
					limit,
					page,
					searchType: {
						value: "referer_uid",
					},
					search: String(uid),
				},
			)
				.then((res) => {
					const data = Array.isArray(res?.items) ? res.items[0] || false : res?.[0] || res || false;
					const currentPlayerPage = getRecoil(playerPageStore);
					if (requestId === this.partnerSettingsRequestId && currentPlayerPage.show && currentPlayerPage.uid === uid) {
						setRecoil(partnerSettingsStore, {
							...getRecoil(partnerSettingsStore),
							data,
							loaded: true,
							error: {
								text: "",
								code: 0,
								timestamp: 0,
							},
						});
					}

					resolve(data);
				})
				.catch((e) => {
					const currentPlayerPage = getRecoil(playerPageStore);
					if (requestId === this.partnerSettingsRequestId && currentPlayerPage.show && currentPlayerPage.uid === uid) {
						setRecoil(partnerSettingsStore, {
							...getRecoil(partnerSettingsStore),
							loaded: true,
							error: e,
						});
					}
					reject(e);
				});
		});
	}

	loadUserPartnerSettingsAdd(data) {
		setRecoil(partnerSettingsStore, {
			...getRecoil(partnerSettingsStore),
			loaded: false,
		});

		this.request("set", { class: "data", module: "partnerSettings", method: "add" }, data)
			.then((res) => {
				this.loadUserPartnerSettingsList(data.refererUid).then(() => {
					this.addNoty("Success", "Partner settings created");
				});
			})
			.catch((e) => {
				setRecoil(partnerSettingsStore, {
					...getRecoil(partnerSettingsStore),
					loaded: true,
					error: e,
				});
			});
	}

	loadUserPartnerSettingsEdit(data) {
		setRecoil(partnerSettingsStore, {
			...getRecoil(partnerSettingsStore),
			loaded: false,
		});

		this.request("set", { class: "data", module: "partnerSettings", method: "edit" }, data)
			.then(() => {
				this.loadUserPartnerSettingsList(data.refererUid);
				setRecoil(partnerSettingsStore, {
					...getRecoil(partnerSettingsStore),
					loaded: true,
					error: {
						text: "",
						code: 0,
						timestamp: 0,
					},
				});
				this.addNoty("Success", "Partner settings updated");
			})
			.catch((e) => {
				setRecoil(partnerSettingsStore, {
					...getRecoil(partnerSettingsStore),
					loaded: true,
					error: e,
				});
			});
	}

	loadTournamentList(project, type, status) {
		setRecoil(tournamentsStore, { ...getRecoil(tournamentsStore), ...getRecoil(tournamentsStore), loaded: false });

		this.request("get", { class: "data", module: "tournaments", method: "list" }, { type, sorting: getRecoil(tournamentsStore).sorting.sortingColumn, search: getRecoil(tournamentsStore).currentSearch, page: getRecoil(tournamentsStore).pagination.current, limit: getRecoil(configStore).tournaments.perPage, project: typeof project !== "undefined" ? project : 0, status })
			.then((tournaments) => {
				setRecoil(tournamentsStore, {
					...getRecoil(tournamentsStore),
					...getRecoil(tournamentsStore),
					pagination: {
						current: tournaments.page,
						total: tournaments.totalPages,
						count: tournaments.total,
					},
					error: {
						text: "",
						code: 0,
						timestamp: 0,
					},
					data: tournaments.data,
					loaded: true,
				});
			})
			.catch((e) => {
				console.log(e);
				setRecoil(tournamentsStore, { ...getRecoil(tournamentsStore), ...getRecoil(tournamentsStore), loaded: true, error: e });
			});
	}

	loadPromoBannerList(project) {
		setRecoil(promoBannersStore, { ...getRecoil(promoBannersStore), ...getRecoil(promoBannersStore), loaded: false });

		this.request("get", { class: "data", module: "promoBanners", method: "list" }, { sorting: getRecoil(promoBannersStore).sorting.sortingColumn, search: getRecoil(promoBannersStore).currentSearch, page: getRecoil(promoBannersStore).pagination.current, limit: getRecoil(configStore).banners.perPage, project: typeof project !== "undefined" ? project : 0 })
			.then((banners) => {
				setRecoil(promoBannersStore, {
					...getRecoil(promoBannersStore),
					...getRecoil(promoBannersStore),
					error: {
						text: "",
						code: 0,
						timestamp: 0,
					},
					data: banners.data.sort((a, b) => b.date - a.date),
					loaded: true,
				});
			})
			.catch((e) => {
				console.log(e);
				setRecoil(promoBannersStore, { ...getRecoil(promoBannersStore), ...getRecoil(promoBannersStore), loaded: true, error: e });
			});
	}

	loadPromoBonuses() {
		setRecoil(promoBonusesStore, { ...getRecoil(promoBonusesStore), loaded: false });

		this.request("get", { class: "data", module: "promoBonuses", method: "list" }, {})
			.then((promoBonuses) => {
				setRecoil(promoBonusesStore, {
					...getRecoil(promoBonusesStore),
					error: {
						text: "",
						code: 0,
						timestamp: 0,
					},
					data: promoBonuses.data,
					loaded: true,
				});
			})
			.catch((e) => {
				console.log(e);
				setRecoil(promoBonusesStore, { ...getRecoil(promoBonusesStore), loaded: true, error: e });
			});
	}

	savePromoBonuses(slides) {
		setRecoil(promoBonusesStore, { ...getRecoil(promoBonusesStore), loaded: false });

		return this.request("set", { class: "data", module: "promoBonuses", method: "save" }, { slides })
			.then((promoBonuses) => {
				setRecoil(promoBonusesStore, {
					...getRecoil(promoBonusesStore),
					error: {
						text: "",
						code: 0,
						timestamp: 0,
					},
					data: promoBonuses.data,
					loaded: true,
				});

				return promoBonuses;
			})
			.catch((e) => {
				console.log(e);
				setRecoil(promoBonusesStore, { ...getRecoil(promoBonusesStore), loaded: true, error: e });
				throw e;
			});
	}

	getEcorAirdropSettings() {
		return this.request("get", { class: "data", module: "ecorAirdrop", method: "getSettings" }, {});
	}

	saveEcorAirdropSettings(settings) {
		return this.request("set", { class: "data", module: "ecorAirdrop", method: "saveSettings" }, settings);
	}

	loadPromoBonusesStatistics(filters = {}) {
		setRecoil(promoBonusesStore, {
			...getRecoil(promoBonusesStore),
			statistics: {
				...getRecoil(promoBonusesStore).statistics,
				loaded: false,
			},
		});

		return this.request("get", { class: "data", module: "promoBonuses", method: "statistics" }, filters)
			.then((statistics) => {
				setRecoil(promoBonusesStore, {
					...getRecoil(promoBonusesStore),
					error: {
						text: "",
						code: 0,
						timestamp: 0,
					},
					statistics: {
						data: Array.isArray(statistics.data) ? statistics.data : [],
						loaded: true,
						summary: statistics.summary || {
							clicks: 0,
							clickToTarget: 0,
							issuedAmount: 0,
							issuedAmountRub: 0,
							targetActions: 0,
							viewToClick: 0,
							views: 0,
						},
					},
				});

				return statistics;
			})
			.catch((e) => {
				console.log(e);
				setRecoil(promoBonusesStore, {
					...getRecoil(promoBonusesStore),
					statistics: {
						...getRecoil(promoBonusesStore).statistics,
						loaded: true,
					},
					error: e,
				});
				throw e;
			});
	}

	loadTournamentsBannerList(project) {
		setRecoil(tournamentsBannersStore, { ...getRecoil(tournamentsBannersStore), ...getRecoil(tournamentsBannersStore), loaded: false });

		this.request("get", { class: "data", module: "tournamentBanners", method: "list" }, { sorting: getRecoil(tournamentsBannersStore).sorting.sortingColumn, search: getRecoil(tournamentsBannersStore).currentSearch, page: getRecoil(tournamentsBannersStore).pagination.current, limit: getRecoil(configStore).banners.perPage, project: typeof project !== "undefined" ? project : 0 })
			.then((banners) => {
				setRecoil(tournamentsBannersStore, {
					...getRecoil(tournamentsBannersStore),
					...getRecoil(tournamentsBannersStore),
					error: {
						text: "",
						code: 0,
						timestamp: 0,
					},
					data: banners.data.sort((a, b) => b.date - a.date),
					loaded: true,
				});
			})
			.catch((e) => {
				console.log(e);
				setRecoil(tournamentsBannersStore, { ...getRecoil(tournamentsBannersStore), ...getRecoil(tournamentsBannersStore), loaded: true, error: e });
			});
	}

	loadChatPinnedList(project) {
		setRecoil(chatPinnedStore, { ...getRecoil(chatPinnedStore), ...getRecoil(chatPinnedStore), loaded: false });

		this.request("get", { class: "data", module: "chatPinned", method: "list" }, { sorting: getRecoil(chatPinnedStore).sorting.sortingColumn, search: getRecoil(chatPinnedStore).currentSearch, page: getRecoil(chatPinnedStore).pagination.current, limit: getRecoil(configStore).chatPinned.perPage, project: typeof project !== "undefined" ? project : 0 })
			.then((chatPinned) => {
				setRecoil(chatPinnedStore, {
					...getRecoil(chatPinnedStore),
					...getRecoil(chatPinnedStore),
					pagination: {
						current: chatPinned.page,
						total: chatPinned.totalPages,
						count: chatPinned.total,
					},
					error: {
						text: "",
						code: 0,
						timestamp: 0,
					},
					data: chatPinned.data,
					loaded: true,
				});
			})
			.catch((e) => {
				console.log(e);
				setRecoil(chatPinnedStore, { ...getRecoil(chatPinnedStore), ...getRecoil(chatPinnedStore), loaded: true, error: e });
			});
	}

	loadBannerList(project) {
		setRecoil(bannersStore, { ...getRecoil(bannersStore), ...getRecoil(bannersStore), loaded: false });

		this.request("get", { class: "data", module: "banners", method: "list" }, { sorting: getRecoil(bannersStore).sorting.sortingColumn, search: getRecoil(bannersStore).currentSearch, page: getRecoil(bannersStore).pagination.current, limit: getRecoil(configStore).banners.perPage, project: typeof project !== "undefined" ? project : 0 })
			.then((banners) => {
				setRecoil(bannersStore, {
					...getRecoil(bannersStore),
					...getRecoil(bannersStore),
					pagination: {
						current: banners.page,
						total: banners.totalPages,
						count: banners.total,
					},
					error: {
						text: "",
						code: 0,
						timestamp: 0,
					},
					data: banners.data,
					loaded: true,
				});
			})
			.catch((e) => {
				console.log(e);
				setRecoil(bannersStore, { ...getRecoil(bannersStore), ...getRecoil(bannersStore), loaded: true, error: e });
			});
	}

	loadHomeBannerList(project) {
		setRecoil(homeBannersStore, { ...getRecoil(homeBannersStore), loaded: false });

		this.request("get", { class: "data", module: "homeBanners", method: "list" }, { sorting: getRecoil(homeBannersStore).sorting.sortingColumn, search: getRecoil(homeBannersStore).currentSearch, page: getRecoil(homeBannersStore).pagination.current, limit: getRecoil(configStore).homeBanners.perPage, project: typeof project !== "undefined" ? project : 0 })
			.then((homeBanners) => {
				setRecoil(homeBannersStore, {
					...getRecoil(homeBannersStore),
					pagination: {
						current: homeBanners.page,
						total: homeBanners.totalPages,
						count: homeBanners.total,
					},
					error: {
						text: "",
						code: 0,
						timestamp: 0,
					},
					data: homeBanners.data,
					loaded: true,
				});
			})
			.catch((e) => {
				console.log(e);
				setRecoil(homeBannersStore, { ...getRecoil(homeBannersStore), loaded: true, error: e });
			});
	}

	loadBannerAnalyticsList() {
		setRecoil(analyticsBannersStore, {
			...getRecoil(analyticsBannersStore),
			loaded: false,
		});

		const filters = {
			sorting: getRecoil(analyticsBannersStore).sorting.sortingColumn,
			search: getRecoil(analyticsBannersStore).currentSearch,
			page: getRecoil(analyticsBannersStore).pagination.current,
			limit: getRecoil(configStore).analyticsBanners.perPage,
			date: {
				from: getRecoil(analyticsBannersStore).sorting.date.startDate,
				to: getRecoil(analyticsBannersStore).sorting.date.endDate,
			},
			productIds: getRecoil(analyticsBannersStore).sorting.products?.map((id) => id.value) || [],
		};

		this.request("get", { class: "data", module: "analytics", method: "banners" }, filters)
			.then((analytics) => {
				setRecoil(analyticsBannersStore, {
					...getRecoil(analyticsBannersStore),
					pagination: {
						current: analytics.page,
						total: analytics.totalPages,
						count: analytics.total,
					},
					error: {
						text: "",
						code: 0,
						timestamp: 0,
					},

					totalClick: analytics.totalClick,
					totalView: analytics.totalView,
					totalCtr: analytics.totalCtr,
					data: analytics.data,
					loaded: true,
				});
			})
			.catch((e) => {
				console.log(e);
				setRecoil(analyticsBannersStore, {
					...getRecoil(analyticsBannersStore),
					loaded: true,
					error: e,
				});
			});
	}

	loadHomeBannerAnalyticsList() {
		setRecoil(analyticsMenuBannersStore, {
			...getRecoil(analyticsMenuBannersStore),
			loaded: false,
		});

		const filters = {
			sorting: getRecoil(analyticsMenuBannersStore).sorting.sortingColumn,
			search: getRecoil(analyticsMenuBannersStore).currentSearch,
			page: getRecoil(analyticsMenuBannersStore).pagination.current,
			limit: getRecoil(configStore).analyticsMenuBanners.perPage,
			date: {
				from: getRecoil(analyticsMenuBannersStore).sorting.date.startDate,
				to: getRecoil(analyticsMenuBannersStore).sorting.date.endDate,
			},
			productIds: getRecoil(analyticsMenuBannersStore).sorting.products?.map((id) => id.value) || [],
		};

		this.request("get", { class: "data", module: "analytics", method: "homeBanners" }, filters)
			.then((analytics) => {
				setRecoil(analyticsMenuBannersStore, {
					...getRecoil(analyticsMenuBannersStore),
					pagination: {
						current: analytics.page,
						total: analytics.totalPages,
						count: analytics.total,
					},
					error: {
						text: "",
						code: 0,
						timestamp: 0,
					},

					totalClick: analytics.totalClick,
					totalView: analytics.totalView,
					totalCtr: analytics.totalCtr,
					data: analytics.data,
					loaded: true,
				});
			})
			.catch((e) => {
				console.log(e);
				setRecoil(analyticsMenuBannersStore, {
					...getRecoil(analyticsMenuBannersStore),
					loaded: true,
					error: e,
				});
			});
	}

	loadReferralCheck(data) {
		return this.request("get", { class: "users", module: "referralCheck", method: "analyze" }, data);
	}

	loadPlayerTagStats(data) {
		return new Promise((resolve, reject) => {
			this.request("get", { class: "panel", module: "playerTag", method: "statistics" }, { ...data })
				.then((stats) => {
					resolve(stats);
				})
				.catch((e) => {
					reject(e);
					setRecoil(playerTagStore, { ...getRecoil(playerTagStore), ...getRecoil(playerTagStore), loaded: true, error: e });
				});
		});
	}
	loadPlayerTagList(project) {
		setRecoil(playerTagStore, { ...getRecoil(playerTagStore), ...getRecoil(playerTagStore), loaded: false });

		this.request("get", { class: "panel", module: "playerTag", method: "list" }, { sorting: getRecoil(playerTagStore).sorting.sortingColumn, searchType: { value: "tag" }, search: getRecoil(playerTagStore).currentSearch, page: getRecoil(playerTagStore).pagination.current, limit: getRecoil(configStore).playerTag.perPage, project: typeof project !== "undefined" ? project : 0 })
			.then((playerRules) => {
				setRecoil(playerTagStore, {
					...getRecoil(playerTagStore),
					...getRecoil(playerTagStore),
					pagination: {
						current: playerRules.pagination.page,
						total: playerRules.pagination.pages,
						count: playerRules.pagination.total,
					},
					error: {
						text: "",
						code: 0,
						timestamp: 0,
					},

					data: playerRules.items,
					loaded: true,
				});
			})
			.catch((e) => {
				console.log(e);
				setRecoil(playerTagStore, { ...getRecoil(playerTagStore), ...getRecoil(playerTagStore), loaded: true, error: e });
			});
	}

	loadBannerAnalyticsInfo(name, date, products) {
		setRecoil(analyticsBannerInfoModalStore, {
			...getRecoil(analyticsBannerInfoModalStore),
			totalClick: 0,
			totalView: 0,
			totalCtr: 0,
			positionSeries: [],
			loaded: false,
		});

		this.request("get", { class: "data", module: "analytics", method: "banners/info" }, { name, date, products })
			.then((analytics) => {
				let startDate;
				let endDate;
				if (analytics.data.length > 0) {
					startDate = new Date(analytics.data[analytics.data.length - 1].date);
					endDate = new Date(analytics.data[0].date);
				}

				setRecoil(analyticsBannerInfoModalStore, {
					...getRecoil(analyticsBannerInfoModalStore),
					loaded: true,
					data: analytics.data,
					positionSeries: analytics.positionSeries || [],
					totalClick: analytics.totalClick,
					totalView: analytics.totalView,
					totalCtr: analytics.totalCtr,
					xDomain: [startDate, endDate],
				});
			})
			.catch((e) => {
				console.log(e);
				setRecoil(analyticsBannerInfoModalStore, {
					...getRecoil(analyticsBannerInfoModalStore),
					loaded: true,
					error: e,
				});
			});
	}

	loadChatPinnedAnalyticsInfo(id, date) {
		return this.request("get", { class: "data", module: "analytics", method: "chatPinned/info" }, { id, date });
	}

	loadHomeBannerAnalyticsInfo(name, date, products) {
		setRecoil(analyticsMenuBannerInfoModalStore, {
			...getRecoil(analyticsMenuBannerInfoModalStore),
			totalClick: 0,
			totalView: 0,
			totalCtr: 0,
			positionSeries: [],
			loaded: false,
		});

		this.request("get", { class: "data", module: "analytics", method: "homeBanners/info" }, { name, date, products })
			.then((analytics) => {
				let startDate;
				let endDate;
				if (analytics.data.length > 0) {
					startDate = new Date(analytics.data[analytics.data.length - 1].date);
					endDate = new Date(analytics.data[0].date);
				}

				setRecoil(analyticsMenuBannerInfoModalStore, {
					...getRecoil(analyticsMenuBannerInfoModalStore),
					loaded: true,
					data: analytics.data,
					positionSeries: analytics.positionSeries || [],
					totalClick: analytics.totalClick,
					totalView: analytics.totalView,
					totalCtr: analytics.totalCtr,
					xDomain: [startDate, endDate],
				});
			})
			.catch((e) => {
				console.log(e);
				setRecoil(analyticsMenuBannerInfoModalStore, {
					...getRecoil(analyticsMenuBannerInfoModalStore),
					loaded: true,
					error: e,
				});
			});
	}

	loadPlayerRulesList(project) {
		setRecoil(playerRulesStore, { ...getRecoil(playerRulesStore), ...getRecoil(playerRulesStore), loaded: false });

		this.request("get", { class: "panel", module: "playerRules", method: "list" }, { sorting: getRecoil(playerRulesStore).sorting.sortingColumn, search: getRecoil(playerRulesStore).currentSearch, page: getRecoil(playerRulesStore).pagination.current, limit: getRecoil(configStore).playerRules.perPage, project: typeof project !== "undefined" ? project : 0 })
			.then((playerRules) => {
				setRecoil(playerRulesStore, {
					...getRecoil(playerRulesStore),
					...getRecoil(playerRulesStore),
					pagination: {
						current: playerRules.pagination.page,
						total: playerRules.pagination.pages,
						count: playerRules.pagination.total,
					},
					error: {
						text: "",
						code: 0,
						timestamp: 0,
					},
					data: playerRules.items,
					loaded: true,
				});
			})
			.catch((e) => {
				console.log(e);
				setRecoil(playerRulesStore, { ...getRecoil(playerRulesStore), ...getRecoil(playerRulesStore), loaded: true, error: e });
			});
	}

	loadFortuneWheelList(project) {
		if (getRecoil(userStore).brand.id !== 1 && getRecoil(userStore).brand.id !== 6) {
			setRecoil(FortuneWheelStore, { ...getRecoil(FortuneWheelStore), ...getRecoil(FortuneWheelStore), loaded: true });

			return;
		}

		if (typeof project !== "undefined" && project !== 0) {
			setRecoil(FortuneWheelStore, { ...getRecoil(FortuneWheelStore), ...getRecoil(FortuneWheelStore), loaded: true });
			return;
		}

		setRecoil(FortuneWheelStore, { ...getRecoil(FortuneWheelStore), ...getRecoil(FortuneWheelStore), loaded: false });

		this.request("get", { class: "users", module: "fortuneWheel", method: "list" }, { sorting: getRecoil(FortuneWheelStore).sorting.sortingColumn, search: getRecoil(FortuneWheelStore).currentSearch, page: getRecoil(FortuneWheelStore).pagination.current, limit: getRecoil(configStore).fortuneWheel.perPage, project: typeof project !== "undefined" ? project : 0 })
			.then((fortune) => {
				setRecoil(FortuneWheelStore, {
					...getRecoil(FortuneWheelStore),
					...getRecoil(FortuneWheelStore),
					pagination: {
						current: fortune.page,
						total: fortune.totalPages,
						count: fortune.total,
					},
					error: {
						text: "",
						code: 0,
						timestamp: 0,
					},
					data: fortune.data,
					loaded: true,
				});
			})
			.catch((e) => {
				console.log(e);
				setRecoil(FortuneWheelStore, { ...getRecoil(FortuneWheelStore), ...getRecoil(FortuneWheelStore), loaded: true, error: e });
			});
	}

	unlinkAccount(uid, social) {
		let body = social;

		if (typeof social.email !== "undefined") {
			body = { uid, email: true };
		} else if (typeof social.phone !== "undefined") {
			body = { uid, phone: true };
		} else {
			body = { social };
		}

		return new Promise((resolve, reject) => {
			this.request("set", { class: "users", module: "unlinkAccount", method: "unlink" }, { uid, ...body })
				.then((social) => {
					resolve(social);
				})
				.catch((e) => {
					console.log(e);
					reject(e);
				});
		});
	}

	startTranslation(data) {
		this.request("get", { class: "data", module: "translation", method: "start" }, { ...data })
			.then((translation) => {
				if (translation.success) {
					this.loadTaskList();
					this.addNoty("Success", translation.status);
				} else {
					this.addNoty("Error", translation.status);
				}
			})
			.catch((e) => {
				console.log(e);
				setRecoil(taskManagerStore, { ...getRecoil(taskManagerStore), loaded: false, error: e });
			});
	}

	loadTaskList() {
		setRecoil(taskManagerStore, { ...getRecoil(taskManagerStore), loaded: false });
		this.request("get", { class: "data", module: "taskManager", method: "list" }, {})
			.then((task) => {
				setRecoil(taskManagerStore, { ...getRecoil(taskManagerStore), data: task.data, loaded: true });
			})
			.catch((e) => {
				console.log(e);
				setRecoil(taskManagerStore, { ...getRecoil(taskManagerStore), loaded: false, error: e });
			});
	}

	loadFortuneWheelWinner(uid, project) {
		if (!this.isCurrentPlayer(uid, project)) return Promise.resolve();

		if (getRecoil(userStore).brand.id !== 1 && getRecoil(userStore).brand.id !== 6) {
			setRecoil(playerPageStore, {
				...getRecoil(playerPageStore),
				uid,
				bonuses: {
					...getRecoil(playerPageStore).bonuses,
					fortuneWheel: {
						...getRecoil(playerPageStore).bonuses.fortuneWheel,
						loaded: true,
					},
				},
			});
			return;
		}

		setRecoil(playerPageStore, {
			...getRecoil(playerPageStore),
			uid,
			bonuses: {
				...getRecoil(playerPageStore).bonuses,
				fortuneWheel: {
					...getRecoil(playerPageStore).bonuses.fortuneWheel,
					loaded: false,
				},
			},
		});

		this.request("get", { class: "users", module: "fortuneWheel", method: "winner" }, { uid, sorting: getRecoil(playerPageStore).bonuses.fortuneWheel.sorting.sortingColumn, search: getRecoil(playerPageStore).bonuses.fortuneWheel.currentSearch, page: getRecoil(playerPageStore).bonuses.fortuneWheel.pagination.current, limit: getRecoil(configStore).players.bonuses.fortuneWheel.perPage, project: typeof project !== "undefined" ? project : 0 })
			.then((fortune) => {
				if (!this.isCurrentPlayer(uid, project)) return;
				setRecoil(playerPageStore, {
					...getRecoil(playerPageStore),
					uid,
					bonuses: {
						...getRecoil(playerPageStore).bonuses,
						fortuneWheel: {
							...getRecoil(playerPageStore).bonuses.fortuneWheel,
							pagination: {
								current: fortune.page,
								total: fortune.totalPages,
								count: fortune.total,
							},
							data: fortune.data,
							loaded: true,
						},
					},
				});
			})
			.catch((e) => {
				if (!this.isCurrentPlayer(uid, project) || e?.name === "AbortError") return;
				console.log(e);
				setRecoil(playerPageStore, {
					...getRecoil(playerPageStore),
					uid,
					bonuses: {
						...getRecoil(playerPageStore).bonuses,
						fortuneWheel: {
							...getRecoil(playerPageStore).bonuses.fortuneWheel,
							loaded: true,
							error: e,
						},
					},
				});
			});
	}

	resetLog(uid, logId, resetAll) {
		//users/wallet/logReset
		this.addNoty("Success", "Operation reset log started , please wait");

		setRecoil(playerPageStore, {
			...getRecoil(playerPageStore),
			log: {
				...getRecoil(playerPageStore).log,
				loaded: false,
			},
		});

		return new Promise((resolve, reject) => {
			this.request("get", { class: "users", module: "wallet", method: "logReset" }, { uid, logId, resetAll })
				.then((data) => {
					this.addNoty("Success", "Log reset success");
					resolve(data);
				})
				.catch((e) => {
					this.addNoty("Error", e.text);
					reject(e);
				});
		});
	}

	loadLogGameDetail(uid, type, date, project, gameID, operations = undefined) {
		try {
			setTimeout(() => {
				setRecoil(gameDetailLogModalStore, {
					...getRecoil(gameDetailLogModalStore),
					loaded: true,
					show: true,
					data: [],
					project,
					uid,
					type,
					date,
					gameID,
					pagination: {
						...getRecoil(gameDetailLogModalStore).pagination,
						count: operations !== undefined ? operations : getRecoil(gameDetailLogModalStore).pagination.count,
						total: operations !== undefined ? Math.ceil(operations / Number(getRecoil(configStore).gameLogDetail.perPage)) : getRecoil(gameDetailLogModalStore).pagination.total,
						current: operations !== undefined ? 1 : getRecoil(gameDetailLogModalStore).pagination.current,
					},
				});
			}, 0);
			return new Promise((resolve, reject) => {
				this.request(
					"get",
					{ class: "users", module: "wallet", method: "logGameDetail" },
					{
						uid,
						data: gameID,
						type,
						date,
						project: typeof project !== "undefined" ? project : 0,
						page: operations !== undefined ? 1 : getRecoil(gameDetailLogModalStore).pagination.current,
						limit: getRecoil(configStore).gameLogDetail.perPage,
					},
				)
					.then((data) => {
						setRecoil(gameDetailLogModalStore, { ...getRecoil(gameDetailLogModalStore), loaded: false, data });
					})
					.catch((e) => {
						console.log(e);
					});
			});
		} catch (error) {
			console.log(error);
		}
	}

	loadLogDetail(uid, type, date, project, operationData) {
		return new Promise((resolve, reject) => {
			this.request("get", { class: "users", module: "wallet", method: "logDetail" }, { uid, type, date, data: operationData, project: typeof project !== "undefined" ? project : 0 })
				.then((data) => {
					resolve(data);
				})
				.catch((e) => {
					console.log(e);
				});
		});
	}

	loadPlayerStats(casinoID, project) {
		return new Promise((resolve, reject) => {
			this.request("get", { class: "users", module: "data", method: "getPlayerStats" }, { casinoID, light: false, project })
				.then((data) => {
					resolve(data);
				})
				.catch((e) => {
					this.addNoty("Error", e.text);
					reject(e);
				});
		});
	}

	loadPlayerSession(casinoID, brandCaisnoId, project) {
		return new Promise((resolve, reject) => {
			this.request("get", { class: "users", module: "data", method: "getPlayerSession" }, { casinoID, brandCaisnoId, project, light: false })
				.then((data) => {
					resolve(data);
				})
				.catch((e) => {
					this.addNoty("Error", e.text);
					reject(e);
				});
		});
	}

	loadTotalCasinoBalance(casinoID, project) {
		return new Promise((resolve, reject) => {
			this.request("get", { class: "users", module: "data", method: "getCasinoBalance" }, { casinoID, light: false, project })
				.then((data) => {
					resolve(data);
				})
				.catch((e) => {
					this.addNoty("Error", e.text);
					reject(e);
				});
		});
	}

	loadExternalCheckRules(uid, project) {
		return new Promise((resolve, reject) => {
			this.request("get", { class: "users", module: "data", method: "checkRulesExternal" }, { uid, project })
				.then((data) => {
					resolve(data);
				})
				.catch((e) => {
					this.addNoty("Error", e.text);
					reject(e);
				});
		});
	}
	loadCheckRules(uid, project) {
		return new Promise((resolve, reject) => {
			this.request("get", { class: "users", module: "data", method: "checkRules" }, { uid, project })
				.then((data) => {
					resolve(data);
				})
				.catch((e) => {
					this.addNoty("Error", e.text);
					reject(e);
				});
		});
	}
	loadTotalReferer(uid, project) {
		return new Promise((resolve, reject) => {
			this.request("get", { class: "users", module: "data", method: "totalReferer" }, { uid, project })
				.then((data) => {
					resolve(data);
				})
				.catch((e) => {
					this.addNoty("Error", e.text);
					reject(e);
				});
		});
	}

	loadTotalRakeback(uid, project) {
		return new Promise((resolve, reject) => {
			this.request("get", { class: "rakeback", module: "data", method: "total" }, { uid, project })
				.then((data) => {
					resolve(data);
				})
				.catch((e) => {
					this.addNoty("Error", e.text);
					reject(e);
				});
		});
	}

	loadTotalPurchasedTickets(uid, project) {
		return new Promise((resolve, reject) => {
			this.request("get", { class: "data", module: "prize", method: "getTotalPlayerPurchased" }, { uid, project })
				.then((data) => {
					resolve(data);
				})
				.catch((e) => {
					this.addNoty("Error", e.text);
					reject(e);
				});
		});
	}

	getPlayerTickets(data) {
		return new Promise((resolve, reject) => {
			this.request("get", { class: "data", module: "prize", method: "getLogsTickets" }, data)
				.then((tickets) => {
					resolve(tickets);
				})
				.catch((e) => {
					this.addNoty("Error", e.text);
					reject(e);
				});
		});
	}

	getTicketsConfig(project = 0) {
		return new Promise((resolve, reject) => {
			this.request("get", { class: "data", module: "prize", method: "getTicketsConfig" }, { project })
				.then((config) => {
					resolve(config);
				})
				.catch((e) => {
					console.log("e: ", e);
					this.addNoty("Error", e.text || e.message || e.toString());
					reject(e);
				});
		});
	}

	getGiftTicketsStats(data) {
		return new Promise((resolve, reject) => {
			this.request("get", { class: "data", module: "prize", method: "getGiftTicketsStats" }, data)
				.then((stats) => {
					resolve(stats);
				})
				.catch((e) => {
					this.addNoty("Error", e.text || e.message || e.toString());
					reject(e);
				});
		});
	}

	updateTicketsConfig(data) {
		return new Promise((resolve, reject) => {
			this.request("set", { class: "data", module: "prize", method: "updateTicketsConfig" }, data)
				.then((config) => {
					resolve(config);
				})
				.catch((e) => {
					this.addNoty("Error", e.text || e.message || e.toString());
					reject(e);
				});
		});
	}

	writeOffTickets(data) {
		return new Promise((resolve, reject) => {
			this.request("set", { class: "data", module: "prize", method: "writeOffTickets" }, data)
				.then((result) => {
					resolve(result);
				})
				.catch((e) => {
					this.addNoty("Error", e.text || e.message || e.toString());
					reject(e);
				});
		});
	}

	loadTotalVkBonus(uid, project) {
		return new Promise((resolve, reject) => {
			this.request("get", { class: "users", module: "data", method: "getTotalVkBonus" }, { uid, project })
				.then((data) => {
					resolve(data);
				})
				.catch((e) => {
					this.addNoty("Error", e.text);
					reject(e);
				});
		});
	}

	loadTotalBoost(uid, project) {
		return new Promise((resolve, reject) => {
			this.request("get", { class: "users", module: "data", method: "getTotalBoost" }, { uid, project })
				.then((data) => {
					resolve(data);
				})
				.catch((e) => {
					this.addNoty("Error", e.text);
					reject(e);
				});
		});
	}

	loadTotalAff(uid, project) {
		return new Promise((resolve, reject) => {
			this.request("get", { class: "users", module: "data", method: "getTotalAff" }, { uid, project })
				.then((data) => {
					resolve(data);
				})
				.catch((e) => {
					this.addNoty("Error", e.text);
					reject(e);
				});
		});
	}

	loadTotalPromocode(uid, project) {
		return new Promise((resolve, reject) => {
			this.request("get", { class: "users", module: "data", method: "getTotalPromocode" }, { uid, project })
				.then((data) => {
					resolve(data);
				})
				.catch((e) => {
					this.addNoty("Error", e.text);
					reject(e);
				});
		});
	}

	loadTotalWheel(uid, project) {
		return new Promise((resolve, reject) => {
			this.request("get", { class: "users", module: "data", method: "getTotalWheel" }, { uid, project })
				.then((data) => {
					resolve(data);
				})
				.catch((e) => {
					this.addNoty("Error", e.text);
					reject(e);
				});
		});
	}

	loadPeriodStatsDeposit(uid, project) {
		return new Promise((resolve, reject) => {
			this.request("get", { class: "data", module: "deposits", method: "statsByPeriod" }, { uid, project })
				.then((data) => {
					resolve(data);
				})
				.catch((e) => {
					this.addNoty("Error", e.text);
					reject(e);
				});
		});
	}

	loadPeriodStatsWithdrawal(uid, project) {
		return new Promise((resolve, reject) => {
			this.request("get", { class: "data", module: "withdrawals", method: "statsByPeriod" }, { uid, project })
				.then((data) => {
					resolve(data);
				})
				.catch((e) => {
					this.addNoty("Error", e.text);
					reject(e);
				});
		});
	}

	loadMultiWithdrawalInfo(uid, type, project, pagination = {}) {
		if (!this.isCurrentPlayer(uid, project)) return Promise.resolve();

		const currentState = getRecoil(multiWithdrawalStore);
		const samePlayerProject = currentState.uid === uid && String(currentState.project) === String(project);
		setRecoil(multiWithdrawalStore, { ...currentState, uid, type, project, loaded: false });

		let page = pagination.page ?? (samePlayerProject ? currentState.multiWithdrawals.pagination.current : 1);
		let limit = pagination.limit ?? 50;

		if (type === "multiPlayers") {
			page = pagination.page ?? (samePlayerProject ? currentState.multiPlayers.pagination.current : 1);
			limit = pagination.limit ?? 50;
		}

		page = Math.max(1, Number(page) || 1);
		limit = Number(limit);

		this.request("get", { class: "users", module: "data", method: "multiWithdrawals" }, { uid, type, page, limit, project })
			.then((data) => {
				if (!this.isCurrentPlayer(uid, project)) return;
				const currentRequest = getRecoil(multiWithdrawalStore);
				if (currentRequest.uid !== uid || currentRequest.type !== type || String(currentRequest.project) !== String(project)) return;

				if (type === "multiPlayers") {
					data.multiPlayers = data.multiPlayers.reduce((accumulator, current) => {
						current.data.forEach((item) => {
							accumulator.push({ ...item, project: current.project });
						});
						return accumulator;
					}, []);

					setRecoil(multiWithdrawalStore, {
						...getRecoil(multiWithdrawalStore),
						multiAccount: { ...data },
						multiPlayers: { pagination: { current: data.pagination.page, total: data.pagination.total, totalPage: data.pagination.totalPage } },
						loaded: true,
					});
				} else {
					data.withdrawals = data.withdrawals.reduce((accumulator, current) => {
						current.data.forEach((item) => {
							accumulator.push({ ...item, project: current.project });
						});
						return accumulator;
					}, []);

					setRecoil(multiWithdrawalStore, {
						...getRecoil(multiWithdrawalStore),
						multiAccount: { ...data },
						multiWithdrawals: { pagination: { current: data.pagination.page, total: data.pagination.total, totalPage: data.pagination.totalPage } },
						loaded: true,
					});
				}
			})
			.catch((e) => {
				if (!this.isCurrentPlayer(uid, project) || e?.name === "AbortError") return;
				console.log(e);
				const currentRequest = getRecoil(multiWithdrawalStore);
				if (currentRequest.uid !== uid || currentRequest.type !== type || String(currentRequest.project) !== String(project)) return;
				setRecoil(multiWithdrawalStore, { ...currentRequest, loaded: true, error: e });
			});
	}
	loadLog(uid, project) {
		const normalizedProject = typeof project !== "undefined" ? project : 0;
		const currentPlayerPage = getRecoil(playerPageStore);
		if (!currentPlayerPage.show || currentPlayerPage.uid !== uid || String(currentPlayerPage.data?.project || 0) !== String(normalizedProject)) return Promise.resolve();

		const requestId = ++this.playerLogRequestId;
		setRecoil(playerPageStore, { ...currentPlayerPage, uid, log: { ...currentPlayerPage.log, loaded: false } });
		//data.filter((item) => Number(item.id))
		return this.request("get", { class: "users", module: "wallet", method: "log" }, { uid, perPage: getRecoil(configStore).players.log.perPage, page: getRecoil(playerPageStore).log.page, type: getRecoil(playerPageStore).log.type, min: getRecoil(playerPageStore).log.min, max: getRecoil(playerPageStore).log.max, limit: getRecoil(configStore).players.log.perPage, project: normalizedProject })
			.then((data) => {
				const latestPlayerPage = getRecoil(playerPageStore);
				if (requestId !== this.playerLogRequestId || !latestPlayerPage.show || latestPlayerPage.uid !== uid || String(latestPlayerPage.data?.project || 0) !== String(normalizedProject)) return;

				setRecoil(playerPageStore, {
					...latestPlayerPage,
					pagination: {
						current: 1,
						total: Math.ceil(
							data.filter((item) => {
								return typeof item.mainItem === "undefined";
							}).length / getRecoil(configStore).players.log.perPage,
						),
					},
					log: { ...latestPlayerPage.log, loaded: true, data: data.filter((item) => Number(item.id)) },
				});
			})
			.catch((e) => {
				if (e?.name === "AbortError") return;
				const latestPlayerPage = getRecoil(playerPageStore);
				if (requestId !== this.playerLogRequestId || !latestPlayerPage.show || latestPlayerPage.uid !== uid || String(latestPlayerPage.data?.project || 0) !== String(normalizedProject)) return;
				console.log(e);
				setRecoil(playerPageStore, { ...latestPlayerPage, log: { ...latestPlayerPage.log, loaded: true, error: e } });
			});
	}

	loadGameLog(casinoID, sessionID, gameID, pageSize = 15) {
		setRecoil(playerGameLogStore, { ...getRecoil(playerGameLogStore), sessionID, gameID, show: true, loaded: false, pagination: { current: 1, total: 0 }, data: [], count: 50 });
		const project = getRecoil(playerPageStore)?.data?.project !== undefined ? getRecoil(playerPageStore)?.data.project : 0;
		pageSize = Math.max(1, Number(pageSize) || 15);

		this.request("get", { class: "users", module: "data", method: "log" }, { casinoID, sessionID, project: typeof project !== "undefined" ? project : 0 })
			.then((data) => {
				const currentLog = getRecoil(playerGameLogStore);
				if (currentLog.sessionID !== sessionID || currentLog.gameID !== gameID) return;

				setRecoil(playerGameLogStore, { ...currentLog, loaded: true, pagination: { current: 1, total: Math.ceil(data.data.length / pageSize) }, data: data.data, from: data.from, to: data.to, initialData: data.data });
			})
			.catch((e) => {
				setRecoil(playerPageStore, { ...getRecoil(playerPageStore), loaded: true, error: e });
			});
	}

	loadSessionsLog(casinoID, gameID, project, uid, pageSize = 15) {
		setRecoil(playerSessionsModalStore, { ...getRecoil(playerSessionsModalStore), gameID, uid, project, show: true, loaded: false, pagination: { current: 1, total: 0 }, data: [] });
		pageSize = Math.max(1, Number(pageSize) || 15);

		this.request("get", { class: "users", module: "data", method: "sessions" }, { casinoID, gameID, project: typeof project !== "undefined" ? project : 0, uid })
			.then((data) => {
				const currentSessions = getRecoil(playerSessionsModalStore);
				if (currentSessions.gameID !== gameID || currentSessions.uid !== uid || String(currentSessions.project) !== String(project)) return;

				setRecoil(playerSessionsModalStore, { ...currentSessions, loaded: true, pagination: { current: 1, total: Math.ceil(data.length / pageSize) }, data: data });
			})
			.catch((e) => {
				console.log(e);
				setRecoil(playerSessionsModalStore, { ...getRecoil(playerSessionsModalStore), loaded: true, error: e });
			});
	}

	loadPlayerFreespins(casinoId, page = 1, uid, status) {
		if (!this.isCurrentPlayer(uid)) return Promise.resolve();

		const playerPageState = getRecoil(playerPageStore);

		setRecoil(playerPageStore, {
			...playerPageState,
			freespins: {
				...playerPageState.freespins,
				data: [],
				loaded: false,
				error: { text: "", code: 0, timestamp: 0 },
			},
		});

		const currentConfig = getRecoil(configStore);
		const limit = currentConfig.freespins.perPage;
		const offset = (page - 1) * limit;

		this.request(
			"get",
			{ class: "data", module: "freespins", method: "list" },
			{
				casinoId: casinoId,
				limit: limit,
				offset: offset,
				uid,
				status,
			},
		)
			.then((data) => {
				if (!this.isCurrentPlayer(uid)) return;
				setRecoil(playerPageStore, {
					...getRecoil(playerPageStore),
					freespins: {
						...getRecoil(playerPageStore).freespins,
						loaded: true,
						data: data.data || [],
						pagination: {
							currentPage: data.pagination?.currentPage || page,
							hasNext: data.pagination?.hasNext || false,
							hasPrevious: data.pagination?.hasPrevious || false,
							limit: data.pagination?.limit || limit,
							offset: data.pagination?.offset || offset,
							count: data.pagination?.count || 0,
						},
						error: { text: "", code: 0, timestamp: 0 },
					},
				});
			})
			.catch((error) => {
				if (!this.isCurrentPlayer(uid) || error?.name === "AbortError") return;
				setRecoil(playerPageStore, {
					...getRecoil(playerPageStore),
					freespins: {
						...getRecoil(playerPageStore).freespins,
						loaded: true,
						data: [],
						error,
					},
				});
			});
	}

	loadSportsbookFreebets(casinoID, uid, project) {
		if (!this.isCurrentPlayer(uid)) return Promise.resolve();

		const playerPageState = getRecoil(playerPageStore);

		setRecoil(playerPageStore, {
			...playerPageState,
			sportsbookFreebets: {
				...playerPageState.sportsbookFreebets,
				data: [],
				loaded: false,
				error: { text: "", code: 0, timestamp: 0 },
			},
		});

		return this.request("get", { class: "users", module: "data", method: "getSportsbookFreebets" }, { casinoID, project })
			.then((data) => {
				if (!this.isCurrentPlayer(uid)) return;
				setRecoil(playerPageStore, {
					...getRecoil(playerPageStore),
					sportsbookFreebets: {
						...getRecoil(playerPageStore).sportsbookFreebets,
						loaded: true,
						data: Array.isArray(data) ? data : [],
						error: { text: "", code: 0, timestamp: 0 },
					},
				});
			})
			.catch((error) => {
				if (!this.isCurrentPlayer(uid) || error?.name === "AbortError") return;
				setRecoil(playerPageStore, {
					...getRecoil(playerPageStore),
					sportsbookFreebets: {
						...getRecoil(playerPageStore).sportsbookFreebets,
						loaded: true,
						data: [],
						error,
					},
				});
			});
	}

	createPlayerFreespins(casinoId, freespinData, uid, status) {
		const requestData = {
			casinoId: casinoId,
			gameId: freespinData.gameId,
			providerId: freespinData.providerId,
			spinsCount: freespinData.spinsCount,
			betLevel: freespinData.betLevel,
			currency: freespinData.currency,
			bonusName: freespinData.bonusName,
			expiresAt: freespinData.expiresAt,
		};

		return this.request("set", { class: "data", module: "freespins", method: "create" }, requestData).then((data) => {
			this.addNoty("Success", `Freespins created successfully! ID: ${data.freespinId}`);
			if (typeof uid !== "undefined" && status) {
				this.loadPlayerFreespins(casinoId, 1, uid, status);
			}
			return data;
		});
	}

	sendCustomerCallcenterEvent(uid, project, payload) {
		return this.request("set", { class: "users", module: "data", method: "customerCallcenterEvent" }, { uid, project, payload }).then((data) => {
			this.addNoty("Success", "Callcenter event sent successfully");
			return data;
		});
	}

	loadFreespinsRules(params = {}) {
		return this.request("get", { class: "data", module: "freespins/rules", method: "list" }, params);
	}

	loadFreespinsLogs(params = {}) {
		return this.request("get", { class: "data", module: "freespins/logs", method: "list" }, params);
	}

	loadFreespinsAnalytics(params = {}) {
		return this.request("get", { class: "data", module: "freespins", method: "analytics" }, params);
	}

	loadFreespinsSettings(params = {}) {
		return this.request("get", { class: "data", module: "freespins", method: "settings" }, params);
	}

	updateFreespinsSettings(payload) {
		return this.request("set", { class: "data", module: "freespins", method: "settings" }, payload);
	}

	createFreespinsRule(payload) {
		return this.request("set", { class: "data", module: "freespins/rules", method: "create" }, payload);
	}

	updateFreespinsRule(payload) {
		return this.request("set", { class: "data", module: "freespins/rules", method: "update" }, payload);
	}

	checkPlayerFreespins(payload) {
		return this.request("set", { class: "data", module: "freespins", method: "check" }, payload);
	}

	vipGrantPlayerFreespins(payload) {
		return this.request("set", { class: "data", module: "freespins", method: "vipGrant" }, payload);
	}
}

export default new Interface();
